import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../../../../../utils/api";
import { motion } from "framer-motion";
import JSZip from "jszip";
import Table from "../../../../ui/Table";
import Modal from "../../../../ui/Modal";
import Loader from "../../../../ui/Loader";
import { swalError, swalSuccess, swalConfirm } from "../../../../../utils/swalAlert";
import Swal from "sweetalert2";
import MobileCapture from "../../../../ui/MobileCapture";
import FileUploadSection from "../../../../ui/FileUploadSection";
import SmartSplitDocumentViewer from "./SmartSplitDocumentViewer";
import { AuthContext } from "../../../../../context/AuthContext";
import { PDFDocument, degrees } from "pdf-lib";
const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

/**
 * LRU Thumbnail Cache & Throttled Render Queue
 * Eliminates GPU texture exhaustion by converting scratch canvas renders to
 * lightweight compressed JPEG blobs displayed via <img> tags, bypassing persistent
 * HTML5 <canvas> hardware raster allocations on integrated GPUs.
 */
const THUMB_CACHE_MAX_ENTRIES = 60;
const thumbnailBlobCache = new Map(); // key `${pageNum}_${rotation}` -> { url, lastUsed }

export const invalidateThumbnailCache = () => {
  for (const [, entry] of thumbnailBlobCache.entries()) {
    try {
      URL.revokeObjectURL(entry.url);
    } catch (_) {}
  }
  thumbnailBlobCache.clear();
};

export const invalidateSingleThumbnail = (pageNum) => {
  for (const [key, entry] of thumbnailBlobCache.entries()) {
    if (key.startsWith(`${pageNum}_`)) {
      try {
        URL.revokeObjectURL(entry.url);
      } catch (_) {}
      thumbnailBlobCache.delete(key);
    }
  }
};

class PdfRenderQueue {
  constructor(concurrency = 2) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  enqueue(id, taskFn, cancelFn) {
    this.dequeue(id);

    return new Promise((resolve, reject) => {
      // Prioritize recently requested items (LIFO) so current visible cards render first
      this.queue.unshift({
        id,
        taskFn,
        cancelFn,
        resolve,
        reject,
      });
      this.processNext();
    });
  }

  dequeue(id) {
    const idx = this.queue.findIndex((item) => item.id === id);
    if (idx !== -1) {
      const item = this.queue.splice(idx, 1)[0];
      if (item.cancelFn) {
        try { item.cancelFn(); } catch (_) {}
      }
      item.resolve(null);
    }
  }

  processNext() {
    if (this.running >= this.concurrency || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    if (!item) return;

    this.running++;
    item
      .taskFn()
      .then((res) => {
        item.resolve(res);
      })
      .catch((err) => {
        item.reject(err);
      })
      .finally(() => {
        this.running--;
        this.processNext();
      });
  }

  clear() {
    while (this.queue.length > 0) {
      const item = this.queue.pop();
      if (item?.cancelFn) {
        try { item.cancelFn(); } catch (_) {}
      }
      item?.resolve(null);
    }
  }
}

const globalPdfRenderQueue = new PdfRenderQueue(2);

/**
 * LandscapeThumbnailCard
 * Lightweight, hardware-friendly page card:
 * 1. Checks LRU cache for existing image blob.
 * 2. If not cached, renders to a temporary scratch canvas at scale 0.28.
 * 3. Immediately calls page.cleanup() to free PDF.js worker memory.
 * 4. Converts to JPEG blob URL and frees scratch canvas GPU memory (width=0, height=0).
 * 5. Displays an <img> element (zero persistent canvas GPU textures).
 */
const LandscapeThumbnailCard = ({
  pdfDocRef,
  pageNumber,
  displayIndex,
  rotation,
  isQrPage,
  isQrBackside,
  onRotatePage,
  onDeletePage,
  onExpandPage,
}) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let currentRenderTask = null;
    const cacheKey = `${pageNumber}_${rotation % 360}`;

    // 1. Check LRU cache first
    if (thumbnailBlobCache.has(cacheKey)) {
      const entry = thumbnailBlobCache.get(cacheKey);
      entry.lastUsed = Date.now();
      setThumbUrl(entry.url);
      setLoading(false);
      return;
    }

    const pdfDoc = pdfDocRef?.current;
    if (!pdfDoc) return;

    setLoading(true);
    const taskId = `page_${pageNumber}_rot_${rotation}`;

    const renderTaskFn = async () => {
      if (cancelled) return null;

      const page = await pdfDoc.getPage(pageNumber);
      if (cancelled) {
        try { page.cleanup(); } catch (_) {}
        return null;
      }

      const defaultVp = page.getViewport({ scale: 1 });
      const baseRotation = defaultVp.rotation || 0;
      const targetRotation =
        (baseRotation + (typeof rotation === "number" ? rotation : 0)) % 360;

      const vp = page.getViewport({ scale: 0.28, rotation: targetRotation });

      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      currentRenderTask = page.render({ canvasContext: ctx, viewport: vp });
      await currentRenderTask.promise;

      // Free PDF.js worker font & glyph cache immediately
      try { page.cleanup(); } catch (_) {}

      if (cancelled) {
        canvas.width = 0;
        canvas.height = 0;
        return null;
      }

      // Convert scratch canvas to lightweight JPEG blob
      const blob = await new Promise((res) => {
        canvas.toBlob(res, "image/jpeg", 0.8);
      });

      // Free scratch canvas backing store immediately from GPU
      canvas.width = 0;
      canvas.height = 0;

      if (!blob || cancelled) return null;

      const url = URL.createObjectURL(blob);

      // Enforce LRU cache bound
      if (thumbnailBlobCache.size >= THUMB_CACHE_MAX_ENTRIES) {
        let oldestKey = null;
        let oldestTime = Infinity;
        for (const [k, v] of thumbnailBlobCache.entries()) {
          if (v.lastUsed < oldestTime) {
            oldestTime = v.lastUsed;
            oldestKey = k;
          }
        }
        if (oldestKey) {
          const evicted = thumbnailBlobCache.get(oldestKey);
          try { URL.revokeObjectURL(evicted.url); } catch (_) {}
          thumbnailBlobCache.delete(oldestKey);
        }
      }

      thumbnailBlobCache.set(cacheKey, { url, lastUsed: Date.now() });
      return url;
    };

    const cancelFn = () => {
      cancelled = true;
      if (currentRenderTask) {
        try { currentRenderTask.cancel(); } catch (_) {}
      }
    };

    globalPdfRenderQueue
      .enqueue(taskId, renderTaskFn, cancelFn)
      .then((url) => {
        if (!cancelled && url) {
          setThumbUrl(url);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled && err?.name !== "RenderingCancelledException") {
          console.warn(`[LandscapeThumbnailCard] Page ${pageNumber} render error:`, err);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      globalPdfRenderQueue.dequeue(taskId);
      if (currentRenderTask) {
        try { currentRenderTask.cancel(); } catch (_) {}
      }
    };
  }, [pdfDocRef, pageNumber, rotation]);

  const displayNum = displayIndex || pageNumber;

  return (
    <div className="landscape-thumb-card">
      <div
        className="landscape-thumb-canvas-wrap"
        onClick={() => onExpandPage?.(pageNumber)}
        style={{ cursor: "pointer" }}
        title={`Click to enlarge page #${displayNum}`}
      >
        {loading || !thumbUrl ? (
          <div className="landscape-thumb-skeleton" />
        ) : (
          <img
            src={thumbUrl}
            alt={`Page ${displayNum}`}
            className="landscape-thumb-img"
            style={{ display: "block" }}
          />
        )}
        {!loading && thumbUrl && (
          <button
            type="button"
            className="landscape-thumb-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              onExpandPage?.(pageNumber);
            }}
            title={`Enlarge page #${displayNum}`}
          >
            <i className="fa-solid fa-arrow-up-right-from-square" />
          </button>
        )}
      </div>

      <div className="landscape-thumb-footer-row">
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <span className="landscape-thumb-page-badge">#{displayNum}</span>
          {isQrPage && (
            <span
              className="cbse-badge"
              style={{ background: "#7c3aed", color: "#fff", padding: "1px 5px", fontSize: "0.68rem" }}
              title="QR Code Cover Sheet (Kept at 0°)"
            >
              QR Cover
            </span>
          )}
          {isQrBackside && (
            <span
              className="cbse-badge"
              style={{ background: "#64748b", color: "#fff", padding: "1px 5px", fontSize: "0.68rem" }}
              title="Backside of QR Sheet (Kept at 0°)"
            >
              QR Back
            </span>
          )}
          {displayIndex && displayIndex !== pageNumber && (
            <span
              className="landscape-thumb-orig-badge"
              title={`Original page #${pageNumber} in scanned document`}
            >
              Orig #{pageNumber}
            </span>
          )}
        </div>
        <span className="landscape-thumb-rot-badge">{rotation % 360}°</span>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <button
            type="button"
            className="landscape-thumb-card-rot-btn"
            onClick={() => {
              invalidateSingleThumbnail(pageNumber);
              onRotatePage?.(pageNumber);
            }}
            title={`Rotate page #${displayNum} 90° clockwise`}
          >
            <i className="fa-solid fa-rotate-right" />
          </button>
          <button
            type="button"
            className="landscape-thumb-card-del-btn"
            onClick={() => {
              invalidateSingleThumbnail(pageNumber);
              onDeletePage?.(pageNumber);
            }}
            title={`Delete page #${displayNum} from document`}
          >
            <i className="fa-solid fa-trash-can" />
          </button>
        </div>
      </div>
    </div>
  );
};


/**
 * LandscapeVirtualGrid
 * Viewport-driven virtualized grid container:
 * - Dynamically calculates responsive column count and row height.
 * - Only mounts DOM nodes for pages currently visible in/near the scroll viewport (12–16 cards).
 * - Maintains 100% native scrollbar accuracy and smooth 60 FPS scrolling.
 */
const LandscapeVirtualGrid = ({
  pdfDocRef,
  pageCount,
  deletedPages,
  pageRotations,
  qrDetectedPages,
  onRotatePage,
  onDeletePage,
  onExpandPage,
}) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 1100,
    height: 600,
  });

  const activePages = useMemo(() => {
    const list = [];
    for (let p = 1; p <= pageCount; p++) {
      if (!deletedPages.has(p)) {
        list.push(p);
      }
    }
    return list;
  }, [pageCount, deletedPages]);

  // Track container dimensions with ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateDims = () => {
      setContainerDimensions({
        width: el.clientWidth || 1100,
        height: el.clientHeight || 600,
      });
    };

    updateDims();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        updateDims();
      });
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, []);

  // Throttled scroll listener with requestAnimationFrame
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setScrollTop(el.scrollTop);
        rafId = null;
      });
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Geometry calculations
  const CARD_WIDTH = 250;
  const CARD_HEIGHT = 220;
  const GAP_X = 24;
  const GAP_Y = 22;
  const ROW_HEIGHT = CARD_HEIGHT + GAP_Y;

  const availableWidth = Math.max(300, containerDimensions.width - 56);
  const columns = Math.max(1, Math.floor((availableWidth + GAP_X) / (CARD_WIDTH + GAP_X)));
  const totalRows = Math.ceil(activePages.length / columns);
  const totalHeight = totalRows > 0 ? totalRows * ROW_HEIGHT : 0;

  // Window range with 1 row overscan above and 1 row below
  const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 1);
  const endRow = Math.min(
    totalRows - 1,
    Math.ceil((scrollTop + containerDimensions.height) / ROW_HEIGHT) + 1
  );

  const startIndex = startRow * columns;
  const endIndex = Math.min(activePages.length, (endRow + 1) * columns);
  const visiblePages = activePages.slice(startIndex, endIndex);
  const topOffset = startRow * ROW_HEIGHT;

  if (activePages.length === 0) {
    return (
      <div ref={containerRef} className="landscape-preview-grid">
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b", width: "100%" }}>
          <i className="fa-solid fa-trash-can" style={{ fontSize: "2rem", marginBottom: "12px", color: "#cbd5e1" }} />
          <p style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600 }}>All pages marked as deleted</p>
          <p style={{ margin: "6px 0 0 0", fontSize: "0.82rem" }}>
            Click &quot;Restore Deleted&quot; above to restore excluded pages back into the document.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="landscape-preview-grid">
      <div
        style={{
          height: `${totalHeight}px`,
          width: "100%",
          position: "relative",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            transform: `translateY(${topOffset}px)`,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: `${GAP_Y}px ${GAP_X}px`,
            pointerEvents: "auto",
          }}
        >
          {visiblePages.map((pageNum, idx) => {
            const activeIdx = startIndex + idx;
            const rotation =
              pageRotations[pageNum] !== undefined
                ? pageRotations[pageNum]
                : pageNum % 2 !== 0
                ? 90
                : 270;
            const isQrPage = qrDetectedPages && qrDetectedPages.includes(pageNum);
            const isQrBackside = qrDetectedPages && qrDetectedPages.some((q) => q + 1 === pageNum);
            return (
              <LandscapeThumbnailCard
                key={`page-${pageNum}`}
                pdfDocRef={pdfDocRef}
                pageNumber={pageNum}
                displayIndex={activeIdx + 1}
                rotation={rotation}
                isQrPage={isQrPage}
                isQrBackside={isQrBackside}
                onRotatePage={onRotatePage}
                onDeletePage={onDeletePage}
                onExpandPage={onExpandPage}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * EnlargedPageCanvas
 * Renders a high-resolution canvas of a single page inside the enlarged preview modal.
 * Optimized with explicit page cleanup and zero-dimension canvas surface reset on unmount.
 */
const EnlargedPageCanvas = ({ pdfDocRef, pageNumber, rotation }) => {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;
    const pdfDoc = pdfDocRef?.current;
    const canvasEl = canvasRef.current;
    if (!pdfDoc || !pageNumber || !canvasEl) return;

    setLoading(true);

    const renderEnlarged = async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (cancelled) {
          try { page.cleanup(); } catch (_) {}
          return;
        }

        const defaultVp = page.getViewport({ scale: 1 });
        const baseRotation = defaultVp.rotation || 0;
        const targetRotation =
          (baseRotation + (typeof rotation === "number" ? rotation : 0)) % 360;

        // Scale 1.35 for crisp, clear readability of student answers and headers
        const scale = 1.35;
        const vp = page.getViewport({ scale, rotation: targetRotation });
        if (!canvasEl || cancelled) {
          try { page.cleanup(); } catch (_) {}
          return;
        }

        canvasEl.width = Math.floor(vp.width);
        canvasEl.height = Math.floor(vp.height);
        const ctx = canvasEl.getContext("2d", { alpha: false });
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);

        renderTask = page.render({ canvasContext: ctx, viewport: vp });
        await renderTask.promise;

        try { page.cleanup(); } catch (_) {}
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled && err?.name !== "RenderingCancelledException") {
          console.warn("[EnlargedPageCanvas] Render error:", err);
          setLoading(false);
        }
      }
    };

    renderEnlarged();

    return () => {
      cancelled = true;
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch (_) {}
      }
      if (canvasEl) {
        canvasEl.width = 0;
        canvasEl.height = 0;
      }
    };
  }, [pdfDocRef, pageNumber, rotation]);

  return (
    <div
      className="enlarged-page-canvas-container"
      style={{
        position: "relative",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "auto",
        background: "#f1f5f9",
        padding: "16px",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#1e293b",
            fontSize: "0.9rem",
            fontWeight: 500,
            background: "rgba(255, 255, 255, 0.95)",
            padding: "8px 18px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            backdropFilter: "blur(4px)",
            zIndex: 10,
          }}
        >
          <i className="fa-solid fa-spinner fa-spin" style={{ color: "#f09a21" }} />
          Rendering page...
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
          borderRadius: "4px",
          background: "#ffffff",
        }}
      />
    </div>
  );
};


export default function PendingAssessments({
  testId,
  isManualEntryTest = false,
  isOnlineExamination=false,
}) {
  const [testInfo, setTestInfo] = useState(null);
  const { user } = useContext(AuthContext);
  const [students, setStudents] = useState([]);
  const [pendingData, setPendingData] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({
    studentId: "",
    firstName: "",
    lastName: "",
    answerPaper: null,
    capturedFile: null,
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkUploadMethod, setBulkUploadMethod] = useState(null); // 'zip', 'folder', 'multiple'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processingBulk, setProcessingBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkPhase, setBulkPhase] = useState("");
  const [currentStudentProcessing, setCurrentStudentProcessing] = useState("");
  const [bulkPrefix, setBulkPrefix] = useState(""); // optional prefix for matching
  const [institute, setInstitute] = useState(null);
  const [qrPdfFile, setQrPdfFile] = useState(null);
  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const abortControllerRef = useRef(null);
  const bulkProgressEndRef = useRef(null);
  const ocrWorkerRef = useRef(null);

  // QR Summary Modal States
  const [qrSummaryData, setQrSummaryData] = useState([]);
  const [isQrSummaryModalOpen, setIsQrSummaryModalOpen] = useState(false);
  const [qrSummaryFile, setQrSummaryFile] = useState(null);
  const [skippedStudents, setSkippedStudents] = useState(new Set());
  // QR CBSE Rotation States
  const [qrRotationMode, setQrRotationMode] = useState("none"); // 'none' | 'cbse'
  const [qrDetectedPages, setQrDetectedPages] = useState([]); // Detected QR page numbers (1-based)

  // Smart Split States
  const [smartSplitFile, setSmartSplitFile] = useState(null);
  const [smartSplitOriginalFile, setSmartSplitOriginalFile] = useState(null);
  const [smartSplitIsCaptured, setSmartSplitIsCaptured] = useState(false);
  const [captureSource, setCaptureSource] = useState(null); // 'single' | 'smart-split'
  const [smartSplitRotationMode, setSmartSplitRotationMode] = useState("none"); // 'none' | 'cbse'
  const [cbsePageRotations, setCbsePageRotations] = useState({}); // { [pageNumber]: angle }
  const [cbseDeletedPages, setCbseDeletedPages] = useState(new Set()); // Set of original 1-based page numbers excluded from scan
  const [isRotatingPdf, setIsRotatingPdf] = useState(false);
  const [landscapePageCount, setLandscapePageCount] = useState(0);
  const [isLandscapePreviewOpen, setIsLandscapePreviewOpen] = useState(false);
  const [enlargedPageNumber, setEnlargedPageNumber] = useState(null); // original 1-based page number for full-view modal
  const landscapePdfDocRef = useRef(null);
  const landscapeAbortRef = useRef(false);
  const [smartSplitProcessingMode, setSmartSplitProcessingMode] = useState("online"); // 'online' | 'batch'
  const [smartSplitHasHeader, setSmartSplitHasHeader] = useState(false);
  const [isHeaderPreviewOpen, setIsHeaderPreviewOpen] = useState(false);
  const [isSmartSplitModalOpen, setIsSmartSplitModalOpen] = useState(false);
  const [smartSplitResult, setSmartSplitResult] = useState(null); // { pages, papers }
  const smartSplitPollRef = useRef(null);

  // Cleanup PDF doc & thumbnail blobs on component unmount
  useEffect(() => {
    return () => {
      invalidateThumbnailCache();
      if (landscapePdfDocRef.current) {
        try {
          landscapePdfDocRef.current.destroy();
        } catch (_) {}
        landscapePdfDocRef.current = null;
      }
    };
  }, []);

  const navigate = useNavigate();

    const fetchInstitute = useCallback(async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        setInstitute(data);
      } catch (err) {
        swalError("Failed to load grade configuration.");
      } finally {
        setLoading(false);
      }
    }, [user.instituteId]);

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = `${serverUrl}/api/answer-paper/get/pending-submissions/test/${testId}`;
      const response = await fetchWithAuth(endpoint, {
        method: "GET",
        credentials: "include",
      });
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch pending submissions");
      const data = await response.json();
      setPendingData(data);
      setHasFetched(true); // Mark as fetched successfully
    } catch (error) {
      console.error("Error fetching pending submissions:", error);
      swalError("Error fetching pending submissions");
      setHasFetched(true); // Still mark as fetched to avoid infinite loading, but don't redirect on error
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    fetchPending();
    fetchInstitute();
  }, [fetchPending, fetchInstitute]);

  // Auto-scroll for bulk progress
  useEffect(() => {
    if (processingBulk && bulkProgressEndRef.current) {
      bulkProgressEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [bulkProgress, processingBulk, bulkPhase, currentStudentProcessing]);

  // If there are no pending submissions after loading, redirect to submitted assessments
  useEffect(() => {
    if (
      hasFetched &&
      !loading &&
      Array.isArray(pendingData) &&
      pendingData.length === 0
    ) {
      const submittedPath = `/instructor/test-grading/${testId}`;
      navigate(submittedPath, { replace: true });
    }
  }, [hasFetched, loading, pendingData, navigate, testId]);

  useEffect(() => {
    const loadLibraries = async () => {
      try {
        // PDF.js
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            script.async = true;
            script.onload = () => {
              window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
              resolve();
            };
            script.onerror = () => reject(new Error("Failed to load PDF.js"));
            document.head.appendChild(script);
          });
        }

        // jsQR
        if (!window.jsQR) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js";
            script.async = true;
            script.onload = resolve;
            script.onerror = () => reject(new Error("Failed to load jsQR"));
            document.head.appendChild(script);
          });
        }

        setLibrariesLoaded(true);
      } catch (err) {
        console.error("Library loading error:", err);
      }
    };

    loadLibraries();
  }, []);

  useEffect(() => {
    return () => {
      if (ocrWorkerRef.current) {
        ocrWorkerRef.current
          .terminate()
          .catch((error) =>
            console.warn("Failed to terminate OCR worker cleanly", error),
          );
        ocrWorkerRef.current = null;
      }
    };
  }, []);

  // Fetch test and student details
  useEffect(() => {
    const fetchTestInfo = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${testId}`,
          {
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch test info");
        const data = await response.json();
        setTestInfo(data);
      } catch (err) {
        console.error("Error fetching test info:", err);
      }
    };

    if (testId) {
      fetchTestInfo();
    }
  }, [testId]);

  // Fetch students when testInfo is available
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${serverUrl}/api/enrollment/get-all/${testInfo?.classId}`,
          {
            credentials: "include",
          },
        );

        if (!response) return;

        if (!response.ok) throw new Error("Failed to fetch students");
        const data = await response.json();

        // Add fullName by joining firstName and lastName
        const transformedData = data.map((student) => ({
          ...student,
          name: `${student.firstName} ${student.lastName}`,
        }));

        setStudents(transformedData);
      } catch (err) {
        swalError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (testInfo?.classId) {
      fetchStudents();
    }
  }, [testInfo?.classId]);

  const openModal = (student) => {
    setSelectedStudent({
      studentId: student.studentId,
      firstName: student.firstName,
      lastName: student.lastName,
      answerPaper: null,
      capturedFile: null,
      url: student.url || null,
      fileName: student.fileName || null,
      answerPaperId: student.answerPaperId || null,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    abortUpload(); // Abort any ongoing bulk upload process
    setIsModalOpen(false);
    setSelectedStudent({
      studentId: "",
      firstName: "",
      lastName: "",
      answerPaper: null,
      capturedFile: null,
    });
    setIsSubmitting(false);
  };

  const resetBulkUploadState = () => {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch (e) {}
      abortControllerRef.current = null;
    }
    setProcessingBulk(false);
    setBulkPhase("");
    setBulkProgress("");
    setCurrentStudentProcessing("");
    setIsBulkModalOpen(false);
    setIsQrSummaryModalOpen(false);
    setQrPdfFile(null);
    setQrSummaryData([]);
    setQrSummaryFile(null);
    setSkippedStudents(new Set());
    setSelectedFiles([]);
    setBulkUploadMethod(null);
    setQrRotationMode("none");
    setQrDetectedPages([]);
  };

  const abortUpload = () => {
    resetBulkUploadState();
  };

  const onCaptureComplete = (capturedFile) => {
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(capturedFile);
      window.__fileUploadCallback = null;
    }

    if (capturedFile) {
      if (captureSource === "smart-split") {
        if (capturedFile.size > 500 * 1024 * 1024) {
          swalError("The captured scan exceeds the 500 MB limit for Smart Split. Please try with fewer pages.", {
            title: "File Too Large",
          });
          setIsBulkModalOpen(true);
          return;
        }
        landscapeAbortRef.current = true;
        setSmartSplitFile(capturedFile);
        setSmartSplitOriginalFile(capturedFile);
        setSmartSplitIsCaptured(true);
        setLandscapePageCount(0);
        landscapePdfDocRef.current = null;
        setIsBulkModalOpen(true);
      } else {
        setSelectedStudent((prev) => ({
          ...prev,
          answerPaper: null,
          capturedFile: capturedFile,
        }));
        setIsModalOpen(true);
      }
    } else {
      if (captureSource === "smart-split") {
        setIsBulkModalOpen(true);
      } else {
        setIsModalOpen(true);
      }
    }
    setIsCapturing(false);
    setCaptureSource(null);
  };

  const handleCloseCapture = () => {
    if (captureSource === "smart-split") {
      setIsBulkModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
    setIsCapturing(false);
    setCaptureSource(null);
    window.__fileUploadCallback = null;
  };

  /**
   * Preflight verification helper for any upload flow (Single, QR, SmartSplit, Bulk).
   * Compares the given file (or first 1-5 pages) against the test paper via Gemini.
   * Returns true to continue, or false if user cancelled on mismatch.
   */
  const preflightVerifyScan = async ({
    file,
    enforceFirstPaper = false,
    maxPages = 5,
    customTitle = "This file doesn't seem to match this test",
    proceedBtnText = "Upload Anyway",
    isBulk = false,
    sampleLabel = null,
  }) => {
    const hasNoTestPaper = testInfo && !testInfo.testPaperURL;
    const isVerificationEnabled = testInfo?.verifyAnswerPaperOnUpload === true;
    if (hasNoTestPaper || !isVerificationEnabled || !file) return true;

    try {
      setBulkProgress("Checking if upload matches this exam paper...");
      let fileToVerify = file;
      let pagesCount = 1;

      // If PDF, slice up to maxPages if file is a multi-page PDF
      if (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf")) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const sourcePdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
          const totalPages = sourcePdf.getPageCount();
          pagesCount = Math.min(maxPages, totalPages);

          if (pagesCount > 0 && totalPages > 1) {
            const samplePdf = await PDFDocument.create();
            const pageIndices = Array.from({ length: pagesCount }, (_, i) => i);
            const copiedPages = await samplePdf.copyPages(sourcePdf, pageIndices);
            copiedPages.forEach((p) => samplePdf.addPage(p));
            const sampleBytes = await samplePdf.save();
            fileToVerify = new File([sampleBytes], "sample_preflight.pdf", {
              type: "application/pdf",
            });
          }
        } catch (sliceErr) {
          console.warn("[Preflight] Slicing fallback to original file:", sliceErr);
          fileToVerify = file;
        }
      }

      const verFormData = new FormData();
      verFormData.append("file", fileToVerify);

      const endpoint = `${serverUrl}/api/answer-paper/verify-scan/${testId}${
        enforceFirstPaper ? "?enforceFirstPaper=true" : ""
      }`;

      const verRes = await fetchWithAuth(endpoint, {
        method: "POST",
        body: verFormData,
      });

      if (verRes && verRes.ok) {
        const verData = await verRes.json();
        if (verData?.verified === true && verData?.isMatch === false) {
          const effectiveTestPaperUrl = verData?.testPaperURL || testInfo?.testPaperURL;
          let sampleFileUrl = null;
          try {
            sampleFileUrl = URL.createObjectURL(fileToVerify);
          } catch (e) {}

          const defaultLabel = isBulk
            ? `View Bulk Answer Paper (Pages 1-${pagesCount > 1 ? pagesCount : 5})`
            : "View answer paper";
          const finalSampleLabel = sampleLabel || defaultLabel;

          const result = await Swal.fire({
            html: `
              <div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 4px 0;">
                <div style="width: 58px; height: 58px; margin: 0 auto 16px auto; border-radius: 50%; background: linear-gradient(135deg, #fffbeb, #fef3c7); border: 2px solid #fde68a; display: flex; align-items: center; justify-content: center; color: #f09a21; font-size: 1.5rem; box-shadow: 0 4px 14px rgba(240, 154, 33, 0.22);">
                  <i class="fa-solid fa-file-circle-exclamation"></i>
                </div>

                <h3 style="font-family: 'Montserrat', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0f172a; margin: 0 0 16px 0; line-height: 1.35;">
                  ${customTitle}
                </h3>

                <div style="background: #fdfcf9; border: 1px solid #fed7aa; border-left: 4px solid #f09a21; border-radius: 10px; padding: 12px 14px; margin-bottom: 16px; text-align: left;">
                  <div style="display: flex; align-items: center; gap: 6px; font-size: 0.75rem; font-weight: 700; color: #f09a21; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> AI Verification Insight
                  </div>
                  <div style="font-family: 'Montserrat', sans-serif; font-size: 0.85rem; color: #1e293b; line-height: 1.5;">
                    ${verData.reason || "Content mismatch detected between test paper and the uploaded document."}
                  </div>
                </div>

                <div style="text-align: left; margin-bottom: 6px;">
                  <span style="font-family: 'Montserrat', sans-serif; font-size: 0.78rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">
                    Review Attached Documents:
                  </span>
                </div>

                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${
                    effectiveTestPaperUrl
                      ? `<a href="${effectiveTestPaperUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; color: #0f172a; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                          <span style="display: flex; align-items: center; gap: 10px;">
                            <span style="width: 28px; height: 28px; border-radius: 6px; background: #fffbeb; display: flex; align-items: center; justify-content: center; color: #f09a21; font-size: 0.9rem;">
                              <i class="fa-solid fa-file-lines"></i>
                            </span>
                            Attached Test Paper
                          </span>
                          <span style="color: #f09a21; font-size: 0.8rem; font-weight: 600; display: flex; align-items: center; gap: 4px;">
                            View <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 0.7rem;"></i>
                          </span>
                        </a>`
                      : ""
                  }
                  ${
                    sampleFileUrl
                      ? `<a href="${sampleFileUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; color: #0f172a; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
                          <span style="display: flex; align-items: center; gap: 10px;">
                            <span style="width: 28px; height: 28px; border-radius: 6px; background: #fffbeb; display: flex; align-items: center; justify-content: center; color: #f09a21; font-size: 0.9rem;">
                              <i class="fa-solid fa-file-pdf"></i>
                            </span>
                            ${finalSampleLabel}
                          </span>
                          <span style="color: #f09a21; font-size: 0.8rem; font-weight: 600; display: flex; align-items: center; gap: 4px;">
                            View <i class="fa-solid fa-arrow-up-right-from-square" style="font-size: 0.7rem;"></i>
                          </span>
                        </a>`
                      : ""
                  }
                </div>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: proceedBtnText,
            confirmButtonColor: "#f09a21",
            cancelButtonText: "Cancel Upload",
            cancelButtonColor: "#ef4136",
            reverseButtons: true,
            width: "480px",
            padding: "24px",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              const popup = Swal.getPopup();
              if (popup) {
                popup.style.borderRadius = "24px";
                popup.style.boxShadow = "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)";
              }
              const confirmBtn = Swal.getConfirmButton();
              const cancelBtn = Swal.getCancelButton();
              if (confirmBtn) {
                confirmBtn.style.borderRadius = "100px";
                confirmBtn.style.fontFamily = "'Montserrat', sans-serif";
                confirmBtn.style.fontWeight = "600";
                confirmBtn.style.fontSize = "0.85rem";
                confirmBtn.style.padding = "10px 22px";
                confirmBtn.style.backgroundColor = "#f09a21";
                confirmBtn.style.borderColor = "#f09a21";
                confirmBtn.style.boxShadow = "none";
                confirmBtn.style.outline = "none";
              }
              if (cancelBtn) {
                cancelBtn.style.borderRadius = "100px";
                cancelBtn.style.fontFamily = "'Montserrat', sans-serif";
                cancelBtn.style.fontWeight = "600";
                cancelBtn.style.fontSize = "0.85rem";
                cancelBtn.style.padding = "10px 22px";
                cancelBtn.style.backgroundColor = "#ef4136";
                cancelBtn.style.borderColor = "#ef4136";
                cancelBtn.style.color = "#ffffff";
                cancelBtn.style.boxShadow = "none";
                cancelBtn.style.outline = "none";
              }
            },
          });

          if (sampleFileUrl) {
            try {
              URL.revokeObjectURL(sampleFileUrl);
            } catch (e) {}
          }

          if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
            return false;
          }
        }
      }
    } catch (err) {
      console.warn("[Preflight] Verification error (fail-open):", err);
    }
    return true;
  };

  const uploadAnswerPaper = async (
    student,
    file,
    isCaptured = false,
    isManualEntryTest = false,
    answerPaperId = null,
    isBulk = false,
    signal = null,
  ) => {
    if (!file) {
      console.error("Missing file or student data:", { file, student });
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("studentId", student.studentId);
    formData.append("testId", testId);
    if (student.numberOfPages) {
      formData.append("numberOfPages", student.numberOfPages);
    }
    if (student.scanOrder !== undefined && student.scanOrder !== null) {
      formData.append("scanOrder", student.scanOrder);
    }
    if (student.batchOrder !== undefined && student.batchOrder !== null) {
      formData.append("batchOrder", student.batchOrder);
    }

    if (file) {
      if (isCaptured) {
        if (file instanceof File) {
          formData.append("file", file);
        } else {
          console.error("Captured file is not a File object:", file);
          swalError("Invalid captured file format");
          setIsSubmitting(false);
          return;
        }
      } else {
        formData.append("file", file);
      }
    }

    const endpoint = answerPaperId
      ? `${serverUrl}/api/answer-paper/update/${answerPaperId}`
      : `${serverUrl}/api/answer-paper/add${
          isManualEntryTest ? "?type=manualTest" : ""
        }`;
    const method = answerPaperId ? "PUT" : "POST";

    try {
      const response = await fetchWithAuth(endpoint, {
        method: method,
        credentials: "include",
        body: formData,
        signal,
      });

      if (!response) {
        throw new Error("No response received");
      }

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Upload failed with response:", errorData);
        throw new Error(errorData.message || "Failed to upload answer paper");
      }

      await response.json();

      if (!isBulk) {
        swalSuccess("Answer Paper uploaded successfully");
        closeModal();
      }

      await fetchPending();

      setPendingData((prev) =>
        prev.filter((s) => s.studentId !== student.studentId),
      );
    } catch (error) {
      console.error("Upload error:", error);
      if (isBulk) {
        throw error; // Rethrow for bulk processing to handle
      } else {
        swalError(error.message || "Error uploading answer paper");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedStudent.answerPaper && !selectedStudent.capturedFile) {
      console.error("No file selected for upload");
      swalError("Please select a file to upload");
      return;
    }

    const file = selectedStudent.capturedFile || selectedStudent.answerPaper;
    const isCaptured = !!selectedStudent.capturedFile;

    try {
      setIsSubmitting(true);
      const ok = await preflightVerifyScan({
        file,
        enforceFirstPaper: true,
        maxPages: 5,
        customTitle: "This file doesn't seem to match this test",
        proceedBtnText: "Upload Anyway",
        isBulk: false,
      });
      if (!ok) {
        setIsSubmitting(false);
        return;
      }

      await uploadAnswerPaper(
        selectedStudent,
        file,
        isCaptured,
        isManualEntryTest,
        selectedStudent.answerPaperId,
      );
    } catch (err) {
      setIsSubmitting(false);
    }
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const renderAnswerPaperUpload = () => {
    const currentFileUrl = selectedStudent?.url || null;
    const currentFileName = selectedStudent?.fileName
      ? decodeURIComponent(selectedStudent.fileName)
      : getFileNameFromUrl(currentFileUrl)
        ? decodeURIComponent(getFileNameFromUrl(currentFileUrl))
        : null;

    return (
      <div className="form-group">
        <label>
          {selectedStudent?.firstName} {selectedStudent?.lastName}
        </label>

        <FileUploadSection
          currentFileUrl={currentFileUrl}
          currentFileName={currentFileName}
          isSubmitting={isSubmitting}
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
          onFileChange={(file, isCaptured) => {
            setSelectedStudent((prev) => ({
              ...prev,
              answerPaper: isCaptured ? null : file,
              capturedFile: isCaptured ? file : null,
            }));
          }}
          onCapture={(callback) => {
            setCaptureSource("single");
            setIsCapturing(true);
            setIsModalOpen(false);
            window.__fileUploadCallback = callback;
          }}
          onRemoveSelectedFile={() => {
            setSelectedStudent((prev) => ({
              ...prev,
              answerPaper: null,
              capturedFile: null,
            }));
          }}
          selectedFile={
            selectedStudent.capturedFile || selectedStudent.answerPaper
          }
          isCaptured={selectedStudent.capturedFile !== null}
          browseTitle="Choose File"
          browseDescription="Select PDF, Doc or image file from device"
          captureTitle="Capture with Camera"
          captureDescription="Take photos and convert to PDF"
        />

        <small className="helper-text">
          Upload answer paper (pdf, doc, jpg, png only) or capture using camera
        </small>
      </div>
    );
  };

  const columns = [
        {
          key: "studentName",
          label: "Student Name",
          render: (student) => `${student.firstName} ${student.lastName}`,
        },
        {
          key: "referenceId",
          label: "ID",
        },
        {
          key: "rollNo",
          label: "Roll No.",
        },
        {
          key: "action1",
          label: "",
          render: (student) => (
            <button onClick={() => openModal(student)} className="btn-upload">
              <i className="fa-solid fa-upload"></i> Upload Assessment
            </button>
          ),
          sortable: false,
        },
      ];

  const PDF_MATCH_KEY = "referenceId";

  const handleBulkFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      swalError("Please select files to upload");
      return;
    }

    // Validate file types
    const validFiles = files.filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      return ["pdf", "jpg", "jpeg", "png"].includes(extension);
    });

    if (validFiles.length === 0) {
      swalError("Please select valid files (PDF, JPG, PNG only)");
      return;
    }

    if (validFiles.length !== files.length) {
      // Some files were invalid and have been ignored
    }

    setSelectedFiles(validFiles);
  };

  const handleFolderSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) {
      swalError("Please select a folder with files");
      return;
    }

    // Validate and filter files
    const validFiles = files.filter((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      return ["pdf", "jpg", "jpeg", "png"].includes(extension);
    });

    if (validFiles.length === 0) {
      swalError("No valid files found in the selected folder");
      return;
    }

    setSelectedFiles(validFiles);
  };

  const handleZipFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      swalError("Please select a ZIP file");
      return;
    }

    if (!file.name.endsWith(".zip")) {
      swalError("Please select a valid ZIP file");
      return;
    }

    setSelectedFiles([file]);
  };

  const extractKeyFromFilename = (filename) => {
    // Remove extension and any parent directory paths
    const nameWithoutExt = filename.replace(/\.(pdf|jpg|jpeg|png)$/i, "");
    const parts = nameWithoutExt.split("/");
    return parts[parts.length - 1];
  };

  // Fast pre-screening with cleanup
  const hasDarkRegion = async (page, pageNum) => {
    let canvas = null;
    let ctx = null;

    try {
      const scale = 0.2;
      const viewport = page.getViewport({ scale });

      canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: false });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: ctx,
        viewport,
        intent: "display",
        renderInteractiveForms: false,
      }).promise;

      // Tiny center area
      const x = Math.floor(canvas.width * 0.4);
      const y = Math.floor(canvas.height * 0.3);
      const w = Math.floor(canvas.width * 0.3);
      const h = Math.floor(canvas.height * 0.35);

      const imageData = ctx.getImageData(x, y, w, h);
      const data = imageData.data;

      // Check ONLY a 10x10 grid (100 pixels total)
      let darkCount = 0;
      const gridSize = 10;
      const stepX = Math.floor(w / gridSize);
      const stepY = Math.floor(h / gridSize);

      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const px = gx * stepX;
          const py = gy * stepY;
          const idx = (py * w + px) * 4;

          if (data[idx] < 200) darkCount++; // Just R channel
        }
      }

      const hasQr = darkCount > 6; // 6+ dark pixels in 10x10 grid

      console.log(
        `${hasQr ? "✓" : "✗"} Page ${pageNum}: dark=${darkCount}/100`,
      );

      return hasQr;
    } catch (err) {
      console.error(`Error page ${pageNum}:`, err);
      return false;
    } finally {
      if (ctx) {
        ctx.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
        ctx = null;
      }
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
        canvas.remove();
        canvas = null;
      }
    }
  };

  // Helper function to detect QR code from a PDF page with cleanup
  const detectQRCode = async (page, pageNum) => {
    let canvas = null;
    let context = null;
    let filteredCanvas = null;
    let filteredContext = null;
    let imageData = null;
    let filteredData = null;

    try {
      const scale = 1;
      const viewport = page.getViewport({ scale });
      canvas = document.createElement("canvas");
      context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      // Scan region (center area where QR code is expected)
      const scanBox = {
        x: Math.floor(canvas.width * 0.2),
        y: Math.floor(canvas.height * 0.22),
        w: Math.floor(canvas.width * 0.6),
        h: Math.floor(canvas.height * 0.45),
      };

      // Extract only the scan region for processing
      imageData = context.getImageData(
        scanBox.x,
        scanBox.y,
        scanBox.w,
        scanBox.h,
      );

      // Apply median filter to remove salt-and-pepper noise (white dots in black areas)
      filteredData = applyMedianFilter(imageData);

      // Clear original imageData
      imageData = null;

      // Put filtered data back on canvas for jsQR to process
      filteredCanvas = document.createElement("canvas");
      filteredCanvas.width = canvas.width;
      filteredCanvas.height = canvas.height;
      filteredContext = filteredCanvas.getContext("2d");
      filteredContext.putImageData(filteredData, 0, 0);

      if (window.jsQR) {
        const code = window.jsQR(
          filteredData.data,
          filteredData.width,
          filteredData.height,
          {
            inversionAttempts: "attemptBoth",
          },
        );

        if (code && code.data) {
          console.log(`✓ QR found on page ${pageNum} with jsQR+Median`);
          return { studentId: code.data.trim() };
        }
      }

      return null;
    } catch (err) {
      console.error(`QR detection error on page ${pageNum}:`, err);
      return null;
    } finally {
      // Aggressive cleanup
      if (filteredData) {
        filteredData = null;
      }
      if (imageData) {
        imageData = null;
      }
      if (filteredContext) {
        filteredContext.clearRect(
          0,
          0,
          filteredCanvas?.width || 0,
          filteredCanvas?.height || 0,
        );
        filteredContext = null;
      }
      if (filteredCanvas) {
        filteredCanvas.width = 1;
        filteredCanvas.height = 1;
        filteredCanvas.remove();
        filteredCanvas = null;
      }
      if (context) {
        context.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
        context = null;
      }
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
        canvas.remove();
        canvas = null;
      }
    }
  };

  // Median filter to remove salt-and-pepper noise (white dots in black areas)
  const applyMedianFilter = (imageData) => {
    const data = new Uint8ClampedArray(imageData.data);
    const w = imageData.width;
    const h = imageData.height;
    const output = new Uint8ClampedArray(data.length);

    // Convert to grayscale first
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = gray;
    }

    // Apply 3x3 median filter
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const vals = [];
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            vals.push(data[((y + ky) * w + (x + kx)) * 4]);
          }
        }
        vals.sort((a, b) => a - b);
        const idx = (y * w + x) * 4;
        const median = vals[4]; // Middle value (5th of 9)
        output[idx] = output[idx + 1] = output[idx + 2] = median;
        output[idx + 3] = 255;
      }
    }

    return new ImageData(output, w, h);
  };


  // Renders a cropped region of a pdf.js page to a PNG image blob
  // cropRegion: { xRatio, yRatio, wRatio, hRatio } — all relative to page dimensions
  const renderCroppedPageToImageBlob = async (page, cropRegion, scale = 2.0) => {
    let canvas = null;
    let ctx = null;
    let croppedCanvas = null;
    let croppedCtx = null;
    try {
      const viewport = page.getViewport({ scale });
      canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      ctx = canvas.getContext("2d", { willReadFrequently: true });

      await page.render({ canvasContext: ctx, viewport }).promise;

      croppedCanvas = document.createElement("canvas");
      const cropX = Math.floor(canvas.width * (cropRegion.xRatio ?? 0));
      const cropY = Math.floor(canvas.height * cropRegion.yRatio);
      const cropWidth = Math.floor(canvas.width * (cropRegion.wRatio ?? 1));
      const cropHeight = Math.floor(canvas.height * cropRegion.hRatio);

      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;
      croppedCtx = croppedCanvas.getContext("2d");

      croppedCtx.drawImage(
        canvas,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );

      return await new Promise((resolve, reject) => {
        croppedCanvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Canvas toBlob failed"));
        }, "image/png");
      });
    } finally {
      if (ctx) ctx.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
        canvas.remove();
      }
      if (croppedCtx) croppedCtx.clearRect(0, 0, croppedCanvas?.width || 0, croppedCanvas?.height || 0);
      if (croppedCanvas) {
        croppedCanvas.width = 1;
        croppedCanvas.height = 1;
        croppedCanvas.remove();
      }
    }
  };

  // Helper to extract handwritten "Number of Pages" via OCR.
  // Renders a specific cropped section of the pdf.js page to a PNG image and sends it to the backend.
  const extractNumberOfPagesWithOCR = async (page) => {
    try {
      const pageNum = page.pageNumber;
      
      // Crop only the "Number of Pages" input area in the top-right student info section.
      const cropRegion = { xRatio: 0.50, yRatio: 0.15, wRatio: 0.40, hRatio: 0.10 };
      const imageBlob = await renderCroppedPageToImageBlob(page, cropRegion);

      const imageFile = new File([imageBlob], `qr-page-${pageNum}.png`, {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("file", imageFile);

      const response = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-page-count`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );

      if (!response) return null;
      if (!response.ok) throw new Error("Failed to fetch page count info");

      const data = await response.json();

      if (data.success) {
        return parseInt(data.numberOfPages, 10) ?? null;
      }
      return null;
    } catch (err) {
      console.error("Error fetching sheet count:", err);
      return null;
    }
  };

  // Helper function to create a new PDF from selected pages
  const createPDFFromPages = async (sourceBuffer, pageNumbers) => {
    const { PDFDocument } =
      await import("https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/+esm");
    const sourcePdf = await PDFDocument.load(sourceBuffer);
    const newPdf = await PDFDocument.create();

    for (const pageNum of pageNumbers) {
      const [copiedPage] = await newPdf.copyPages(sourcePdf, [pageNum - 1]);
      newPdf.addPage(copiedPage);
    }

    return await newPdf.save();
  };

  const handleQRFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setQrPdfFile(file);
    }
  };

  const processBulkUpload = async () => {
    if (selectedFiles.length === 0) {
      swalError("Please select files to upload");
      return;
    }

    setProcessingBulk(true);
    setBulkPhase("preparing");
    setBulkProgress("Preparing bulk upload...");
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Build student map using optional prefix when matching by referenceId
      const studentMap = new Map(
        pendingData.map((student) => {
          const baseKey =
            PDF_MATCH_KEY === "referenceId"
              ? student.referenceId
              : student.firstName;
          const mapKey =
            bulkPrefix && PDF_MATCH_KEY === "referenceId"
              ? `${bulkPrefix}${baseKey}`
              : baseKey;
          return [mapKey, student];
        }),
      );

      let filesToProcess = [];

      if (bulkUploadMethod === "zip") {
        // Extract ZIP file
        setBulkPhase("extracting");
        setBulkProgress("Extracting ZIP file...");

        const zip = new JSZip();
        const zipContent = await zip.loadAsync(selectedFiles[0]);

        for (const [filename, fileObj] of Object.entries(zipContent.files)) {
          if (fileObj.dir) continue;

          const extension = filename.split(".").pop().toLowerCase();
          if (!["pdf", "jpg", "jpeg", "png"].includes(extension)) continue;

          const blob = await fileObj.async("blob");
          const file = new File([blob], filename.split("/").pop(), {
            type:
              extension === "pdf"
                ? "application/pdf"
                : `image/${extension === "jpg" ? "jpeg" : extension}`,
          });

          filesToProcess.push(file);
        }
      } else {
        // Direct files (multiple or folder)
        filesToProcess = selectedFiles;
      }

      if (filesToProcess.length === 0) {
        swalError("No valid files found to process");
        setProcessingBulk(false);
        return;
      }

      // Preflight verification against test paper (pages 1-5 of first candidate)
      const sampleFile =
        filesToProcess.find(
          (f) =>
            f.type === "application/pdf" ||
            f.name?.toLowerCase().endsWith(".pdf"),
        ) || filesToProcess[0];

      if (sampleFile) {
        const ok = await preflightVerifyScan({
          file: sampleFile,
          enforceFirstPaper: true,
          maxPages: 5,
          customTitle: "This file doesn't seem to match this test",
          proceedBtnText: "Upload Anyway",
          isBulk: true,
        });
        if (!ok) {
          setProcessingBulk(false);
          setBulkPhase("");
          setBulkProgress("");
          return;
        }
      }

      setBulkPhase("matching");
      setBulkProgress("Matching files with students...");

      // Fetch scan metadata for offset and batch tracking
      let maxScanOrder = 0;
      let nextBatchOrder = 1;
      try {
        const metaRes = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/scan-meta/${testId}`,
          { signal },
        );
        if (metaRes?.ok) {
          const metaData = await metaRes.json();
          maxScanOrder = metaData.maxScanOrder || 0;
          nextBatchOrder = metaData.nextBatchOrder || 1;
        }
      } catch (metaErr) {
        console.warn("[PendingAssessments] Failed to fetch scan-meta:", metaErr);
      }

      let uploaded = 0;
      let skipped = 0;
      const totalFiles = filesToProcess.length;
      const successRecords = [];
      const errorRecords = [];

      for (let i = 0; i < filesToProcess.length; i++) {
        if (signal.aborted) break;
        const file = filesToProcess[i];
        const fileKey = extractKeyFromFilename(file.name);

        // Find matching student
        const student = studentMap.get(fileKey);

        if (student) {
          setBulkPhase("uploading");
          setCurrentStudentProcessing(
            `${student.firstName} ${student.lastName}`,
          );
          setBulkProgress(
            `Uploading for ${student.firstName} ${student.lastName} (${
              uploaded + 1
            }/${totalFiles})...`,
          );
          try {
            if (signal.aborted) throw new Error("AbortError");
            await uploadAnswerPaper(
              {
                ...student,
                scanOrder: maxScanOrder + i + 1,
                batchOrder: nextBatchOrder,
              },
              file,
              false,
              isManualEntryTest,
              student.answerPaperId,
              true,
              signal,
            );
            uploaded++;
            successRecords.push({
              fullName: `${student.firstName} ${student.lastName}`,
              referenceId: student.referenceId || "-",
              fileName: file.name,
            });
          } catch (error) {
            if (
              error.message === "BULK_UPLOAD_STOPPED" ||
              error.name === "AbortError" ||
              error.message === "AbortError"
            ) {
              throw error;
            }
            console.error(`Error uploading for ${student.firstName}:`, error);
            skipped++;
            errorRecords.push({
              fullName: `${student.firstName} ${student.lastName}`,
              referenceId: student.referenceId || "-",
              fileName: file.name,
              error: error.message || "Upload failed",
            });
          }
        } else {
          console.warn(`No matching student found for file: ${file.name}`);
          skipped++;
          errorRecords.push({
            fullName: "Unknown",
            referenceId: "-",
            fileName: file.name,
            error: "No matching student found",
          });
        }

        // Small delay to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setBulkPhase("completed");
      setBulkProgress(
        `Bulk upload completed! Uploaded: ${uploaded} Skipped: ${skipped}`,
      );
      setCurrentStudentProcessing("");

      setTimeout(() => {
        setIsBulkModalOpen(false);
        setSelectedFiles([]);
        setBulkUploadMethod(null);
        fetchPending();

        // Build summary table
        const successRowsHtml = successRecords
          .map(
            (s) =>
              `<tr style="background: #d4edda;">
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.fileName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Success</td>
            </tr>`,
          )
          .join("");

        const errorRowsHtml = errorRecords
          .map(
            (s) =>
              `<tr style="background: #f8d7da;">
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.fileName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
            </tr>`,
          )
          .join("");

        const tableHtml = `
          <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
            <div style="min-width: 600px;">
              <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
                <thead>
                  <tr style="background: #f8f9fa; position: sticky; top: 0;">
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Student Name</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Reference ID</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">File</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${successRowsHtml}
                  ${errorRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
          <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
          <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
            <span style="color: #155724; font-weight: 600;">✓ Success: ${successRecords.length}</span>
            <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
          </div>
        `;

        const icon =
          errorRecords.length === 0
            ? "success"
            : successRecords.length === 0
              ? "error"
              : "info";
        const title =
          errorRecords.length === 0
            ? "All Assessments Uploaded!"
            : successRecords.length === 0
              ? "Upload Failed"
              : "Upload Summary";

        Swal.fire({
          icon,
          title,
          html: tableHtml,
          width: "90%",
          timer: null,
          timerProgressBar: false,
          showConfirmButton: true,
          confirmButtonText: "Got it!",
        });
      }, 1500);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        error.message === "AbortError" ||
        error.message === "BULK_UPLOAD_STOPPED"
      ) {
        console.log("Bulk upload process stopped by user");
        resetBulkUploadState();
        fetchPending();
        return;
      }
      console.error("Error processing bulk upload:", error);
      swalError(error.message || "Error processing bulk upload");
    } finally {
      if (processingBulk) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setCurrentStudentProcessing("");
      }
    }
  };

  // Process QR PDF upload just like bulk upload but with QR code detection
  const processQRPDF = async () => {
    if (!qrPdfFile || !librariesLoaded) return;

    setProcessingBulk(true);
    setBulkPhase("preparing");
    setBulkProgress("Preparing bulk upload...");

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let pdf = null;
    let arrayBuffer = null;

    try {
      // Preflight verify against test paper (pages 1-5)
      const ok = await preflightVerifyScan({
        file: qrPdfFile,
        maxPages: 5,
        customTitle: "This file doesn't seem to match this test",
        proceedBtnText: "Upload Anyway",
        isBulk: true,
      });
      if (!ok) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setQrPdfFile(null);
        return;
      }

      arrayBuffer = await qrPdfFile.arrayBuffer();

      // pdf.js transfers (detaches) the ArrayBuffer it receives, making it unusable afterward.
      // Keep a separate copy for pdf-lib (used in createPDFFromPages via extractNumberOfPagesWithOCR).
      const pdfLibBuffer = arrayBuffer.slice(0);
      arrayBuffer = pdfLibBuffer; // reassign so later code still uses the live copy

      setBulkPhase("extracting");
      setBulkProgress("Reading PDF pages...");

      // Give pdf.js its own copy so it can detach it without affecting pdfLibBuffer
      const loadingTask = window.pdfjsLib.getDocument({ data: pdfLibBuffer.slice(0) });
      pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;

      setBulkPhase("matching");
      setBulkProgress(`Scanning for QR codes (0/${totalPages} pages)...`);

      const studentSections = [];
      const detectedQrPageList = [];
      let currentStudent = null;
      let currentPages = [];
      let skipNextPage = false; // skip the page immediately after each QR page

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (signal.aborted) throw new Error("AbortError");
        setBulkProgress(
          `Scanning for QR codes (${pageNum}/${totalPages} pages)...`,
        );

        // Skip the page that comes right after a QR page (e.g. blank back-side)
        if (skipNextPage) {
          skipNextPage = false;
          console.log(`Skipping page ${pageNum} (page after QR page)`);
          continue;
        }

        const page = await pdf.getPage(pageNum);

        if (signal.aborted) throw new Error("AbortError");

        try {
          // STAGE 1: Quick pre-check
          const mightHaveQR = await hasDarkRegion(page, pageNum);

          if (signal.aborted) throw new Error("AbortError");

          if (!mightHaveQR) {
            if (currentStudent) {
              currentPages.push(pageNum);
            }
            continue;
          }

          // STAGE 2: Full QR detection
          const qrData = await detectQRCode(page, pageNum);

          if (signal.aborted) throw new Error("AbortError");

          if (qrData) {
            detectedQrPageList.push(pageNum);
            setBulkProgress(
              `QR Detected! Getting student details (${pageNum}/${totalPages} pages)...`
            );
            if (institute?.isQRPageCountDetectionEnabled) {
              const PageCountOnPage = await extractNumberOfPagesWithOCR(page);
              qrData.numberOfPages = PageCountOnPage;
            } else {
              qrData.numberOfPages = null;
            }
 
            if (currentStudent && currentPages.length > 0) {
              studentSections.push({
                qrData: currentStudent,
                pages: [...currentPages],
              });
            }
            currentStudent = qrData;
            currentPages = [];
            skipNextPage = true; // mark the next page to be skipped
          } else if (currentStudent) {
            currentPages.push(pageNum);
          }
        } finally {
          // Cleanup page
          if (page && page.cleanup) {
            page.cleanup();
          }

          // GC hint every 10 pages
          if (pageNum % 10 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }
      }

      // Save final section
      if (currentStudent && currentPages.length > 0) {
        studentSections.push({
          qrData: currentStudent,
          pages: [...currentPages],
        });
      }

      if (studentSections.length === 0) {
        swalError(
          "No QR codes found in the PDF. Please ensure your PDF contains valid QR codes.",
        );
        setProcessingBulk(false);
        setBulkPhase("");
        return;
      }

      setBulkProgress("Preparing Summary...");

      const enrichedSummaryData = [];

      for (let i = 0; i < studentSections.length; i++) {
        const section = studentSections[i];
        const { qrData, pages } = section;

        const studentDetails = students.find((s) => s._id === qrData.studentId);

        let enrichedSection = {
          studentId: qrData.studentId,
          numberOfPages: qrData.numberOfPages,
          pages: [...pages],
          pageCount: pages.length,
          pageRange:
            pages.length > 0
              ? pages.length === 1
                ? `${pages[0]}`
                : `${pages[0]}-${pages[pages.length - 1]}`
              : "0",
        };

        if (studentDetails) {
          enrichedSection = {
            ...enrichedSection,
            firstName: studentDetails.firstName,
            lastName: studentDetails.lastName,
            referenceId: studentDetails.referenceId,
            fullName: `${studentDetails.firstName} ${studentDetails.lastName}`,
            isRecognized: true,
          };
        } else {
          enrichedSection = {
            ...enrichedSection,
            firstName: "Unknown",
            lastName: "Student",
            referenceId: "N/A",
            fullName: "Unrecognized QR Data",
            isRecognized: false,
          };
        }

        enrichedSummaryData.push(enrichedSection);
      }

      setQrDetectedPages(detectedQrPageList);

      let masterPdfToUse = qrPdfFile;
      if (qrRotationMode === "cbse") {
        setBulkProgress("Rotating master PDF for CBSE alignment...");
        const qrRotationsMap = calculateQrCbseRotations(totalPages, detectedQrPageList);
        setCbsePageRotations(qrRotationsMap);
        setCbseDeletedPages(new Set());
        setLandscapePageCount(totalPages);

        try {
          const pdfLibDoc = await PDFDocument.load(pdfLibBuffer.slice(0), { ignoreEncryption: true });
          for (let i = 0; i < totalPages; i++) {
            const pageNum = i + 1;
            const rot = qrRotationsMap[pageNum] || 0;
            if (rot !== 0) {
              const page = pdfLibDoc.getPage(i);
              const baseRot = page.getRotation().angle;
              page.setRotation(degrees((baseRot + rot) % 360));
            }
          }
          const rotatedBytes = await pdfLibDoc.save();
          masterPdfToUse = new File(
            [new Blob([rotatedBytes], { type: "application/pdf" })],
            qrPdfFile.name,
            { type: "application/pdf", lastModified: Date.now() }
          );
        } catch (rotErr) {
          console.error("Error applying CBSE rotations to master PDF:", rotErr);
          swalError("Failed to apply CBSE rotations to master PDF. Proceeding with original.");
          masterPdfToUse = qrPdfFile;
        }

        // Auto-open Landscape Preview Modal once upload/processing of QR master PDF is done
        try {
          if (landscapePdfDocRef.current) {
            try {
              landscapePdfDocRef.current.destroy();
            } catch (_) {}
            landscapePdfDocRef.current = null;
          }
          invalidateThumbnailCache();

          const previewLoadingTask = window.pdfjsLib.getDocument({
            data: new Uint8Array(pdfLibBuffer.slice(0)),
            cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
            cMapPacked: true,
          });
          landscapePdfDocRef.current = await previewLoadingTask.promise;
          setIsLandscapePreviewOpen(true);
        } catch (previewErr) {
          console.error("Error auto-opening landscape preview for QR PDF:", previewErr);
        }
      }

      setQrSummaryData(enrichedSummaryData);
      setQrSummaryFile(masterPdfToUse);

      // Auto-skip students where scanned pages don't match expected numberOfPages
      // Only performed when sheet count detection is enabled for the institute.
      // Also auto-skip students who are NOT in pendingData (assessment already available).
      const pendingStudentIds = new Set(pendingData.map((s) => s.studentId));
      const autoSkipped = new Set(
        enrichedSummaryData
          .filter(
            (d) =>
              // Not in pending list => already has assessment
              !pendingStudentIds.has(d.studentId) ||
              // Page count mismatch (only when sheet detection enabled)
              (institute?.isQRPageCountDetectionEnabled &&
                d.isRecognized &&
                d.numberOfPages != null &&
                !isNaN(d.numberOfPages) &&
                d.numberOfPages !== 0 &&
                d.pageCount !== d.numberOfPages),
          )
          .map((d) => d.studentId),
      );
      setSkippedStudents(autoSkipped);
      setIsQrSummaryModalOpen(true);

      // Stop the processing UI here - we continue in confirmAndUploadQR
      setProcessingBulk(false);
      setBulkPhase("");
      setBulkProgress("");
      setCurrentStudentProcessing("");
      setIsBulkModalOpen(false); // Close the bulk upload progress modal
    } catch (error) {
      if (error.name === "AbortError" || error.message === "AbortError") {
        console.log("QR PDF process aborted by user");
        return;
      }
      console.error("QR PDF processing error:", error);
      swalError(error.message || "Error processing QR PDF");
    } finally {
      if (processingBulk) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setCurrentStudentProcessing("");
      }
      // Final cleanup
      if (pdf) {
        pdf.destroy();
        pdf = null;
      }
    }
  };

  // Step 2 of QR PDF Flow: Handle the actual confirmed payload upload
  const confirmAndUploadQR = async () => {
    if (!qrSummaryData || qrSummaryData.length === 0 || !qrSummaryFile) {
      swalError("Missing summary data to perform upload.");
      return;
    }

    setProcessingBulk(true);
    setBulkPhase("preparing");
    setBulkProgress("Preparing bulk upload...");

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let createBuffer = null;

    try {
      setBulkProgress(
        `Creating individual PDFs (0/${qrSummaryData.length} sections)...`,
      );

      let uploaded = 0;
      let skipped = 0;
      const successRecords = [];
      const errorRecords = [];

      // Fetch scan metadata for offset and batch tracking
      let maxScanOrder = 0;
      let nextBatchOrder = 1;
      try {
        const metaRes = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/scan-meta/${testId}`,
          { signal },
        );
        if (metaRes?.ok) {
          const metaData = await metaRes.json();
          maxScanOrder = metaData.maxScanOrder || 0;
          nextBatchOrder = metaData.nextBatchOrder || 1;
        }
      } catch (metaErr) {
        console.warn("[PendingAssessments] Failed to fetch scan-meta:", metaErr);
      }

      createBuffer = await qrSummaryFile.arrayBuffer();

      for (let i = 0; i < qrSummaryData.length; i++) {
        if (signal.aborted) throw new Error("AbortError");

        const section = qrSummaryData[i];

        if (!section.isRecognized) {
          console.warn(
            `Skipping unrecognized student for QR data: ${section.studentId}`,
          );
          skipped++;
          errorRecords.push({
            fullName: "Unrecognized QR",
            referenceId: section.studentId || "-",
            pageRange: section.pageRange || "-",
            error: "Student not found in class",
          });
          continue;
        }

        // Skip students that instructor chose to skip OR who are not in pendingData
        const pendingIds = new Set(pendingData.map((s) => s.studentId));
        if (skippedStudents.has(section.studentId) || !pendingIds.has(section.studentId)) {
          console.log(
            `Skipping student ${section.fullName} (instructor opted to skip or assessment already available)`,
          );
          skipped++;
          continue;
        }

        setBulkProgress(`Creating PDF (${i + 1}/${qrSummaryData.length})...`);

        let pdfBytes = null;
        let blob = null;
        let file = null;

        try {
          // Create PDF blob
          if (signal.aborted) throw new Error("AbortError");
          const pagesToInclude =
            cbseDeletedPages && cbseDeletedPages.size > 0
              ? section.pages.filter((p) => !cbseDeletedPages.has(p))
              : section.pages;

          if (pagesToInclude.length === 0) {
            console.warn(`All pages deleted for student ${section.fullName}`);
            skipped++;
            continue;
          }

          pdfBytes = await createPDFFromPages(createBuffer, pagesToInclude);
          blob = new Blob([pdfBytes], { type: "application/pdf" });

          // Create File object from blob
          if (signal.aborted) throw new Error("AbortError");
          const fileName = `Assessment_${
            section.referenceId && section.referenceId !== "N/A"
              ? section.referenceId
              : section.studentId
          }.pdf`;
          file = new File([blob], fileName, { type: "application/pdf" });

          setBulkPhase("uploading");
          setCurrentStudentProcessing(section.fullName);
          const totalUploads = qrSummaryData.filter(d => d.isRecognized && !skippedStudents.has(d.studentId)).length;
          setBulkProgress(
            `Uploading for ${section.fullName} (${uploaded + 1}/${totalUploads})...`,
          );

          // Create minimal student object with studentId from QR
          const studentObj = {
            studentId: section.studentId,
            numberOfPages: section.numberOfPages,
            scanOrder: maxScanOrder + i + 1,
            batchOrder: nextBatchOrder,
          };

          // DEBUG: SIMULATE ERROR FOR STUDENT INDEX 2
          // if (i === 2) {
          //   throw new Error("Simulated upload error for debugging purposes");
          // }

          if (signal.aborted) throw new Error("AbortError");
          await uploadAnswerPaper(
            studentObj,
            file,
            false,
            isManualEntryTest,
            null,
            true,
            signal,
          );
          uploaded++;
          successRecords.push({
            fullName: section.fullName,
            referenceId: section.referenceId || "-",
            pageRange: section.pageRange || "-",
          });
        } catch (error) {
          if (
            error.message === "BULK_UPLOAD_STOPPED" ||
            error.name === "AbortError" ||
            error.message === "AbortError"
          ) {
            throw error;
          }
          console.error(
            `Error uploading for student ${section.studentId}:`,
            error,
          );
          skipped++;
          errorRecords.push({
            fullName: section.fullName,
            referenceId: section.referenceId || "-",
            pageRange: section.pageRange || "-",
            error: error.message || "Upload failed",
          });
        } finally {
          // Cleanup per student
          pdfBytes = null;
          blob = null;
          file = null;

          // GC hint every 5 students
          if (i % 5 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        }

        // Small delay to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setBulkPhase("completed");
      setBulkProgress(
        `Bulk upload completed! Uploaded: ${uploaded} Skipped: ${skipped}`,
      );
      setCurrentStudentProcessing("");

      setTimeout(() => {
        setIsQrSummaryModalOpen(false);
        setQrPdfFile(null);
        setQrSummaryData([]);
        setQrSummaryFile(null);
        setSkippedStudents(new Set());
        setBulkUploadMethod(null);
        setQrRotationMode("none");
        setQrDetectedPages([]);
        setCbsePageRotations({});
        setCbseDeletedPages(new Set());
        setLandscapePageCount(0);
        setIsLandscapePreviewOpen(false);
        if (landscapePdfDocRef.current) {
          try {
            landscapePdfDocRef.current.destroy();
          } catch (_) {}
          landscapePdfDocRef.current = null;
        }
        fetchPending();

        // Build summary table
        const successRowsHtml = successRecords
          .map(
            (s) =>
              `<tr style="background: #d4edda;">
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.pageRange}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Success</td>
            </tr>`,
          )
          .join("");

        const errorRowsHtml = errorRecords
          .map(
            (s) =>
              `<tr style="background: #f8d7da;">
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.pageRange}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
            </tr>`,
          )
          .join("");

        const tableHtml = `
          <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
            <div style="min-width: 580px;">
              <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
                <thead>
                  <tr style="background: #f8f9fa; position: sticky; top: 0;">
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Student Name</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Reference ID</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Pages</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${successRowsHtml}
                  ${errorRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
          <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
          <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
            <span style="color: #155724; font-weight: 600;">✓ Success: ${successRecords.length}</span>
            <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
          </div>
        `;

        const icon =
          errorRecords.length === 0
            ? "success"
            : successRecords.length === 0
              ? "error"
              : "info";
        const title =
          errorRecords.length === 0
            ? "All Assessments Uploaded!"
            : successRecords.length === 0
              ? "Upload Failed"
              : "Upload Summary";

        Swal.fire({
          icon,
          title,
          html: tableHtml,
          width: "90%",
          timer: null,
          timerProgressBar: false,
          showConfirmButton: true,
          confirmButtonText: "Got it!",
        });
      }, 1500);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        error.message === "AbortError" ||
        error.message === "BULK_UPLOAD_STOPPED"
      ) {
        console.log("QR PDF upload process stopped by user");
        resetBulkUploadState();
        fetchPending();
        return;
      }
      console.error("QR PDF upload error:", error);
      swalError(error.message || "Error finalizing QR PDF");
    } finally {
      if (processingBulk) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setCurrentStudentProcessing("");
      }
    }
  };

  // Process multiple QR PDF files (folder or multiple selection)
  const processQRPDFFiles = async () => {
    if (selectedFiles.length === 0 || !librariesLoaded) return;

    setProcessingBulk(true);
    setBulkPhase("preparing");
    setBulkProgress("Preparing bulk upload...");
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      // Preflight verify against test paper (pages 1-5 of first PDF candidate)
      const sampleFile =
        selectedFiles.find(
          (f) =>
            f.type === "application/pdf" ||
            f.name?.toLowerCase().endsWith(".pdf"),
        ) || selectedFiles[0];

      if (sampleFile) {
        const ok = await preflightVerifyScan({
          file: sampleFile,
          maxPages: 5,
          customTitle: "This file doesn't seem to match this test",
          proceedBtnText: "Upload Anyway",
          isBulk: true,
        });
        if (!ok) {
          setProcessingBulk(false);
          setBulkPhase("");
          setBulkProgress("");
          return;
        }
      }

      setBulkPhase("matching");
      setBulkProgress(
        `Scanning for QR codes (0/${selectedFiles.length} files)...`,
      );

      let uploaded = 0;
      let skipped = 0;
      const totalFiles = selectedFiles.length;
      const successRecords = [];
      const errorRecords = [];

      // Fetch scan metadata for offset and batch tracking
      let maxScanOrder = 0;
      let nextBatchOrder = 1;
      try {
        const metaRes = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/scan-meta/${testId}`,
          { signal },
        );
        if (metaRes?.ok) {
          const metaData = await metaRes.json();
          maxScanOrder = metaData.maxScanOrder || 0;
          nextBatchOrder = metaData.nextBatchOrder || 1;
        }
      } catch (metaErr) {
        console.warn("[PendingAssessments] Failed to fetch scan-meta:", metaErr);
      }

      for (let i = 0; i < selectedFiles.length; i++) {
        if (signal.aborted) throw new Error("AbortError");
        const file = selectedFiles[i];

        // Only process PDF files
        if (file.type !== "application/pdf") {
          console.warn(`Skipping non-PDF file: ${file.name}`);
          skipped++;
          errorRecords.push({
            fullName: "-",
            referenceId: "-",
            fileName: file.name,
            error: "Not a PDF file",
          });
          continue;
        }

        setBulkProgress(
          `Scanning for QR codes (${i + 1}/${totalFiles} files)...`,
        );

        // Load PDF to check total pages and detect QR
        const arrayBuffer = await file.arrayBuffer();
        const arrayBufferCopy = arrayBuffer.slice(0); // Create a copy for later use
        const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        const totalPages = pdf.numPages;
        console.log("totalPages", totalPages);

        // Detect QR code from first page
        const page = await pdf.getPage(1);
        const qrData = await detectQRCode(page, 1);

        if (!qrData || !qrData.studentId) {
          console.warn(`No QR code found in file: ${file.name}`);
          skipped++;
          errorRecords.push({
            fullName: "-",
            referenceId: "-",
            fileName: file.name,
            error: "No QR code found",
          });
          continue;
        }

        // Check if there are pages after the QR page
        if (totalPages === 1) {
          console.warn(
            `File ${file.name} only contains QR page, no content pages found`,
          );
          skipped++;
          errorRecords.push({
            fullName: "-",
            referenceId: "-",
            fileName: file.name,
            error: "Only QR page found, no content",
          });
          continue;
        }

        // Find student details
        const studentDetails = students.find((s) => s._id === qrData.studentId);
        if (!studentDetails) {
          console.warn(
            `No matching student found in class for file: ${file.name}`,
          );
          skipped++;
          errorRecords.push({
            fullName: "-",
            referenceId: "-",
            fileName: file.name,
            error: "Student not found in class",
          });
          continue;
        }

        setBulkPhase("uploading");
        setCurrentStudentProcessing(
          `${studentDetails.firstName} ${studentDetails.lastName}`,
        );
        setBulkProgress(
          `Processing PDF for ${studentDetails.firstName} ${studentDetails.lastName} (${uploaded + 1}/${totalFiles})...`,
        );

        try {
          // If CBSE rotation is on, skip page 1 (QR page) AND page 2 (backside of QR)
          const startPage = qrRotationMode === "cbse" && totalPages > 2 ? 3 : 2;
          const pageNumbers = [];
          for (let p = startPage; p <= totalPages; p++) {
            pageNumbers.push(p);
          }

          if (pageNumbers.length === 0) {
            console.warn(`File ${file.name} has no answer pages after skipping QR pages`);
            skipped++;
            errorRecords.push({
              fullName: `${studentDetails.firstName} ${studentDetails.lastName}`,
              referenceId: studentDetails.referenceId || "-",
              fileName: file.name,
              error: "Only QR pages found, no content",
            });
            continue;
          }

          let bufferToExtract = arrayBufferCopy;
          if (qrRotationMode === "cbse") {
            try {
              const pdfLibDoc = await PDFDocument.load(arrayBufferCopy, { ignoreEncryption: true });
              for (let idx = 0; idx < pageNumbers.length; idx++) {
                const pageNum = pageNumbers[idx];
                const pageIdx = pageNum - 1;
                const rot = idx % 2 === 0 ? 90 : 270;
                const pdfPage = pdfLibDoc.getPage(pageIdx);
                const baseRot = pdfPage.getRotation().angle;
                pdfPage.setRotation(degrees((baseRot + rot) % 360));
              }
              const rotatedBytes = await pdfLibDoc.save();
              bufferToExtract = rotatedBytes.buffer;
            } catch (rotErr) {
              console.error(`Error applying CBSE rotations to file ${file.name}:`, rotErr);
              bufferToExtract = arrayBufferCopy;
            }
          }

          // Use the copy of the ArrayBuffer
          if (signal.aborted) throw new Error("AbortError");
          const pdfBytes = await createPDFFromPages(
            bufferToExtract,
            pageNumbers,
          );
          const blob = new Blob([pdfBytes], { type: "application/pdf" });

          // Create File object from blob
          if (signal.aborted) throw new Error("AbortError");
          const fileName = `Assessment_${
            studentDetails ? studentDetails.referenceId : qrData.studentId
          }.pdf`;
          const newFile = new File([blob], fileName, {
            type: "application/pdf",
          });

          // Create minimal student object with studentId from QR
          const studentObj = {
            studentId: qrData.studentId,
            scanOrder: maxScanOrder + i + 1,
            batchOrder: nextBatchOrder,
          };

          // Upload the modified file (without QR page)
          await uploadAnswerPaper(
            studentObj,
            newFile,
            false,
            isManualEntryTest,
            null, // answerPaperId
            true, // isBulk
            signal,
          );
          uploaded++;
          successRecords.push({
            fullName: `${studentDetails.firstName} ${studentDetails.lastName}`,
            referenceId: studentDetails.referenceId || "-",
            fileName: file.name,
          });
        } catch (error) {
          if (
            error.message === "BULK_UPLOAD_STOPPED" ||
            error.name === "AbortError" ||
            error.message === "AbortError"
          ) {
            throw error;
          }
          console.error(
            `Error uploading for student ${qrData.studentId}:`,
            error,
          );
          skipped++;
          errorRecords.push({
            fullName: studentDetails
              ? `${studentDetails.firstName} ${studentDetails.lastName}`
              : "-",
            referenceId: studentDetails?.referenceId || "-",
            fileName: file.name,
            error: error.message || "Upload failed",
          });
        }

        // Small delay to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setBulkPhase("completed");
      setBulkProgress(
        `Bulk upload completed! Uploaded: ${uploaded} Skipped: ${skipped}`,
      );
      setCurrentStudentProcessing("");

      setTimeout(() => {
        setIsBulkModalOpen(false);
        setSelectedFiles([]);
        setBulkUploadMethod(null);
        fetchPending();

        // Build summary table
        const successRowsHtml = successRecords
          .map(
            (s) =>
              `<tr style="background: #d4edda;">
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724;">✓</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb;">${s.fileName}</td>
              <td style="padding: 8px; border: 1px solid #c3e6cb; color: #155724; font-weight: 600;">Success</td>
            </tr>`,
          )
          .join("");

        const errorRowsHtml = errorRecords
          .map(
            (s) =>
              `<tr style="background: #f8d7da;">
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.fullName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.referenceId}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb;">${s.fileName}</td>
              <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">${s.error}</td>
            </tr>`,
          )
          .join("");

        const tableHtml = `
          <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
            <div style="min-width: 580px;">
              <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
                <thead>
                  <tr style="background: #f8f9fa; position: sticky; top: 0;">
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Student Name</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Reference ID</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">File</th>
                    <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: left;">Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${successRowsHtml}
                  ${errorRowsHtml}
                </tbody>
              </table>
            </div>
          </div>
          <p style="margin-top: 8px; font-size: 0.75em; color: #888; text-align: center;">Swipe horizontally to see all columns</p>
          <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
            <span style="color: #155724; font-weight: 600;">✓ Success: ${successRecords.length}</span>
            <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
          </div>
        `;

        const icon =
          errorRecords.length === 0
            ? "success"
            : successRecords.length === 0
              ? "error"
              : "info";
        const title =
          errorRecords.length === 0
            ? "All Assessments Uploaded!"
            : successRecords.length === 0
              ? "Upload Failed"
              : "Upload Summary";

        Swal.fire({
          icon,
          title,
          html: tableHtml,
          width: "90%",
          timer: null,
          timerProgressBar: false,
          showConfirmButton: true,
          confirmButtonText: "Got it!",
        });
      }, 1500);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        error.message === "AbortError" ||
        error.message === "BULK_UPLOAD_STOPPED"
      ) {
        console.log("QR files process stopped by user");
        resetBulkUploadState();
        fetchPending();
        return;
      }
      console.error("QR PDF files processing error:", error);
      swalError(error.message || "Error processing QR PDF files");
    } finally {
      if (processingBulk) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setCurrentStudentProcessing("");
      }
    }
  };

  // ─── Smart Split: Dynamic Upload with real-time speed/progress & Direct GCS ───
  const uploadFileWithProgress = ({ url, method = 'POST', file, headers = {}, isFormData = true, extraFields = {}, onProgress }) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const startTime = Date.now();
      let lastLoaded = 0;
      let lastTime = startTime;
      let speedMBs = '0.0';

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) {
          const now = Date.now();
          const timeDiffSec = (now - lastTime) / 1000;

          if (timeDiffSec >= 0.5) {
            const bytesDiff = e.loaded - lastLoaded;
            const currentSpeed = (bytesDiff / timeDiffSec) / (1024 * 1024);
            speedMBs = Math.max(0.1, currentSpeed).toFixed(1);
            lastLoaded = e.loaded;
            lastTime = now;
          }

          const pct = Math.round((e.loaded / e.total) * 100);
          const loadedMB = (e.loaded / (1024 * 1024)).toFixed(1);
          const totalMB = (e.total / (1024 * 1024)).toFixed(1);

          if (onProgress) {
            if (pct >= 100) {
              onProgress({
                pct: 100,
                loadedMB,
                totalMB,
                speedMBs,
                message: "Finishing upload...",
              });
            } else {
              const speedText = speedMBs && parseFloat(speedMBs) > 0 ? ` · ${speedMBs} MB/s` : "";
              onProgress({
                pct,
                loadedMB,
                totalMB,
                speedMBs,
                message: `Uploading papers: ${loadedMB} / ${totalMB} MB${speedText}...`,
              });
            }
          }
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const parsed = xhr.responseText ? JSON.parse(xhr.responseText) : {};
            resolve(parsed);
          } catch {
            resolve({});
          }
        } else {
          reject(new Error(`Upload failed with HTTP ${xhr.status}: ${xhr.statusText || xhr.responseText || 'Error'}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during file upload. Check connection or CORS settings.'));
      xhr.onabort = () => reject(new Error('Upload was aborted.'));

      xhr.open(method, url);
      if (url.includes('/api/')) {
        xhr.withCredentials = true;
      }
      Object.entries(headers).forEach(([key, val]) => {
        xhr.setRequestHeader(key, val);
      });

      if (isFormData) {
        const fd = new FormData();
        fd.append('file', file);
        if (extraFields && typeof extraFields === 'object') {
          Object.entries(extraFields).forEach(([k, v]) => fd.append(k, v));
        }
        xhr.send(fd);
      } else {
        xhr.send(file);
      }
    });
  };

  /**
   * Applies per-page rotations and excludes deleted pages from a PDF document using pdf-lib.
   */
  const applyRotationsToFile = async (sourceFile, rotationsMap, numPages, deletedPages = new Set()) => {
    if (!sourceFile) return null;
    const arrayBuffer = await sourceFile.arrayBuffer();
    const pdfLibDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
    const totalPages = numPages || pdfLibDoc.getPageCount();

    // 1. Apply rotations to pages
    for (let i = 0; i < totalPages; i++) {
      const pageNum = i + 1;
      const page = pdfLibDoc.getPage(i);
      const baseRotation = page.getRotation().angle;
      const additionalRotation =
        rotationsMap && typeof rotationsMap[pageNum] === "number"
          ? rotationsMap[pageNum]
          : pageNum % 2 !== 0
          ? 90
          : 270;
      page.setRotation(degrees((baseRotation + additionalRotation) % 360));
    }

    // 2. Remove deleted pages in descending index order to avoid index shift
    if (deletedPages && deletedPages.size > 0) {
      const indicesToRemove = Array.from(deletedPages)
        .map((p) => p - 1)
        .filter((idx) => idx >= 0 && idx < totalPages)
        .sort((a, b) => b - a);

      for (const idx of indicesToRemove) {
        pdfLibDoc.removePage(idx);
      }
    }

    const rotatedBytes = await pdfLibDoc.save();
    return new File(
      [new Blob([rotatedBytes], { type: "application/pdf" })],
      sourceFile.name,
      { type: "application/pdf", lastModified: Date.now() }
    );
  };

  /**
   * Calculates the CBSE alternating rotation map for a QR-coded bulk PDF.
   * - Each detected QR page and its immediate backside (page + 1) are kept unrotated (0°).
   * - For each student's answer booklet following the QR pages:
   *   - 1st answer booklet page (odd) rotates 90° CW.
   *   - 2nd answer booklet page (even) rotates 270° CW.
   *   - 3rd answer booklet page (odd) rotates 90° CW, and so on.
   */
  const calculateQrCbseRotations = (totalPages, detectedQrPages) => {
    const qrSet = new Set(detectedQrPages || []);
    const rotations = {};
    let lastQrPage = null;
    let bookletPageCounter = 0;

    for (let p = 1; p <= totalPages; p++) {
      if (qrSet.has(p)) {
        lastQrPage = p;
        bookletPageCounter = 0;
        rotations[p] = 0; // QR page untouched
      } else if (lastQrPage !== null && p === lastQrPage + 1) {
        rotations[p] = 0; // Backside of QR page untouched
        bookletPageCounter = 0;
      } else if (lastQrPage !== null) {
        bookletPageCounter++;
        rotations[p] = bookletPageCounter % 2 !== 0 ? 90 : 270;
      } else {
        rotations[p] = 0;
      }
    }
    return rotations;
  };

  /**
   * Opens the landscape preview modal for QR bulk PDF from the Review Summary modal.
   */
  const handleOpenQrLandscapePreview = async () => {
    if (!qrPdfFile) return;
    if (landscapePdfDocRef.current) {
      setIsLandscapePreviewOpen(true);
      return;
    }
    setIsRotatingPdf(true);
    try {
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.async = true;
          script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            resolve();
          };
          script.onerror = () => reject(new Error("Failed to load PDF.js"));
          document.head.appendChild(script);
        });
      }

      if (landscapePdfDocRef.current) {
        try {
          landscapePdfDocRef.current.destroy();
        } catch (_) {}
        landscapePdfDocRef.current = null;
      }
      invalidateThumbnailCache();

      const arrayBuffer = await qrPdfFile.arrayBuffer();
      const loadingTask = window.pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
        cMapPacked: true,
      });
      const pdfJsDoc = await loadingTask.promise;
      landscapePdfDocRef.current = pdfJsDoc;
      setIsLandscapePreviewOpen(true);
    } catch (err) {
      console.error("Error opening QR landscape preview:", err);
      swalError("Failed to open preview for QR PDF.");
    } finally {
      setIsRotatingPdf(false);
    }
  };

  /**
   * Toggles CBSE Alternating Rotation Mode (odd pages 90° CW, even pages 270° CW).
   * Opens the preview modal and sets up per-page rotation state.
   */
  const handleToggleCbseRotation = async () => {
    if (smartSplitRotationMode === "cbse") {
      landscapeAbortRef.current = true;
      setSmartSplitRotationMode("none");
      setCbsePageRotations({});
      setCbseDeletedPages(new Set());
      setIsLandscapePreviewOpen(false);
      if (landscapePdfDocRef.current) {
        try {
          landscapePdfDocRef.current.destroy();
        } catch (_) {}
      }
      landscapePdfDocRef.current = null;
      invalidateThumbnailCache();
      if (smartSplitOriginalFile) {
        setSmartSplitFile(smartSplitOriginalFile);
      }
      return;
    }

    const fileToProcess = smartSplitOriginalFile || smartSplitFile;
    if (!fileToProcess) return;

    landscapeAbortRef.current = false;
    setIsRotatingPdf(true);

    try {
      // Ensure PDF.js is loaded
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
          script.async = true;
          script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            resolve();
          };
          script.onerror = () => reject(new Error("Failed to load PDF.js"));
          document.head.appendChild(script);
        });
      }

      // Destroy previous doc if any and invalidate cached thumbnails
      if (landscapePdfDocRef.current) {
        try {
          landscapePdfDocRef.current.destroy();
        } catch (_) {}
        landscapePdfDocRef.current = null;
      }
      invalidateThumbnailCache();

      const arrayBuffer = await fileToProcess.arrayBuffer();

      // Load PDF.js doc from the original file (without cloning slice)
      const loadingTask = window.pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
        cMapPacked: true,
      });
      const pdfJsDoc = await loadingTask.promise;

      if (landscapeAbortRef.current) {
        try {
          pdfJsDoc.destroy();
        } catch (_) {}
        return;
      }

      landscapePdfDocRef.current = pdfJsDoc;
      const numPages = pdfJsDoc.numPages;

      // Initialize default CBSE alternating rotations: Odd pages 90°, Even pages 270°
      const initialMap = {};
      for (let p = 1; p <= numPages; p++) {
        initialMap[p] = p % 2 !== 0 ? 90 : 270;
      }

      setCbsePageRotations(initialMap);
      setCbseDeletedPages(new Set());
      setLandscapePageCount(numPages);
      setSmartSplitRotationMode("cbse");
      setIsLandscapePreviewOpen(true);

      // NOTE: We deliberately DO NOT run applyRotationsToFile() eagerly here.
      // Pre-baking 300 pages via pdf-lib in the background causes a 2-3GB memory spike
      // and is completely wasted if the user customizes or cancels rotation.
      // Rotations are baked Just-In-Time when the user confirms preview or clicks upload.
    } catch (err) {
      if (!landscapeAbortRef.current) {
        console.error("Error setting up CBSE rotation:", err);
        swalError("Failed to process PDF. Please verify the file is a valid PDF.");
        setSmartSplitRotationMode("none");
        setCbsePageRotations({});
        setCbseDeletedPages(new Set());
        setIsLandscapePreviewOpen(false);
        setLandscapePageCount(0);
        if (landscapePdfDocRef.current) {
          try {
            landscapePdfDocRef.current.destroy();
          } catch (_) {}
          landscapePdfDocRef.current = null;
        }
        invalidateThumbnailCache();
      }
    } finally {
      setIsRotatingPdf(false);
    }
  };

  /**
   * Rotates an individual page by an additional 90° clockwise in the preview.
   */
  const handleRotateIndividualPage = (pageNum) => {
    invalidateSingleThumbnail(pageNum);
    setCbsePageRotations((prev) => {
      const current =
        prev[pageNum] !== undefined ? prev[pageNum] : pageNum % 2 !== 0 ? 90 : 270;
      const next = (current + 90) % 360;
      return { ...prev, [pageNum]: next };
    });
  };

  /**
   * Deletes an individual page from the CBSE scan preview.
   * Prompts the teacher for confirmation and excludes the page from the final output PDF.
   */
  const handleDeletePage = async (pageNum) => {
    const remainingCount = landscapePageCount - cbseDeletedPages.size;
    if (remainingCount <= 1) {
      swalError("Cannot delete the only remaining page. The document must have at least one page.", {
        title: "Cannot Delete Page",
      });
      return;
    }

    const confirmResult = await swalConfirm(
      "Delete Page?",
      `Are you sure you want to remove Page #${pageNum} from this document? It will be excluded from the final split.`,
      "Yes, Delete",
      "Cancel"
    );

    if (confirmResult?.isConfirmed) {
      invalidateSingleThumbnail(pageNum);
      const nonDeleted = Array.from({ length: landscapePageCount }, (_, idx) => idx + 1).filter(
        (p) => !cbseDeletedPages.has(p)
      );
      const currentIdx = nonDeleted.indexOf(pageNum);
      const nextTarget = nonDeleted[currentIdx + 1] || nonDeleted[currentIdx - 1] || null;

      setCbseDeletedPages((prev) => {
        const next = new Set(prev);
        next.add(pageNum);
        return next;
      });

      // If this page was currently open in enlarged view, advance or close
      if (enlargedPageNumber === pageNum) {
        setEnlargedPageNumber(nextTarget);
      }
    }
  };

  /**
   * Restores all deleted pages back to the document.
   */
  const handleRestoreDeletedPages = () => {
    setCbseDeletedPages(new Set());
  };

  /**
   * Resets all pages to the standard CBSE alternating rotation (odd 90°, even 270°) and restores any deleted pages.
   */
  const handleResetCbseRotations = () => {
    invalidateThumbnailCache();
    const defaultMap = {};
    if (bulkUploadMethod === "qr-pdf" && qrDetectedPages && qrDetectedPages.length > 0) {
      const qrMap = calculateQrCbseRotations(landscapePageCount, qrDetectedPages);
      Object.assign(defaultMap, qrMap);
    } else {
      for (let p = 1; p <= landscapePageCount; p++) {
        defaultMap[p] = p % 2 !== 0 ? 90 : 270;
      }
    }
    setCbsePageRotations(defaultMap);
    setCbseDeletedPages(new Set());
    setEnlargedPageNumber(null);
  };

  /**
   * Keyboard shortcuts listener for enlarged preview navigation (Left/Right arrow, Escape)
   */
  useEffect(() => {
    if (enlargedPageNumber === null) return;

    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;

      const nonDeletedPages = Array.from({ length: landscapePageCount }, (_, idx) => idx + 1).filter(
        (p) => !cbseDeletedPages.has(p)
      );
      const currentIndex = nonDeletedPages.indexOf(enlargedPageNumber);

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (currentIndex > 0) {
          setEnlargedPageNumber(nonDeletedPages[currentIndex - 1]);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (currentIndex < nonDeletedPages.length - 1) {
          setEnlargedPageNumber(nonDeletedPages[currentIndex + 1]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setEnlargedPageNumber(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enlargedPageNumber, landscapePageCount, cbseDeletedPages]);

  /**
   * Confirms landscape preview adjustments and saves rotated & pruned file.
   */
  const handleConfirmLandscapePreview = async () => {
    setIsLandscapePreviewOpen(false);
    setEnlargedPageNumber(null);
    try {
      if (bulkUploadMethod === "qr-pdf") {
        const source = qrPdfFile;
        if (source && qrRotationMode === "cbse") {
          const rotated = await applyRotationsToFile(
            source,
            cbsePageRotations,
            landscapePageCount,
            new Set() // Keep all pages in master so section.pages indexing is preserved, cbseDeletedPages is filtered during createPDFFromPages
          );
          if (rotated) {
            setQrSummaryFile(rotated);
          }
        }
      } else {
        const source = smartSplitOriginalFile || smartSplitFile;
        if (source && smartSplitRotationMode === "cbse") {
          const rotated = await applyRotationsToFile(
            source,
            cbsePageRotations,
            landscapePageCount,
            cbseDeletedPages
          );
          if (rotated) {
            setSmartSplitFile(rotated);
          }
        }
      }
    } catch (e) {
      console.warn("Failed to bake rotations on confirm:", e);
    }
  };

  const handleSmartSplitUpload = async () => {
    if (!smartSplitFile) return;

    const MAX_SMART_SPLIT_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB limit
    if (smartSplitFile.size > MAX_SMART_SPLIT_SIZE_BYTES) {
      swalError("The selected file exceeds the 500 MB limit for Smart Split. Please select a file smaller than 500 MB.", {
        title: "File Too Large",
      });
      return;
    }

    setProcessingBulk(true);
    setBulkPhase("uploading");
    setBulkProgress("Preparing upload...");

    let fileToUpload = smartSplitFile;
    if (smartSplitRotationMode === "cbse") {
      setBulkProgress("Applying page rotations to PDF...");
      try {
        fileToUpload = await applyRotationsToFile(
          smartSplitOriginalFile || smartSplitFile,
          cbsePageRotations,
          landscapePageCount,
          cbseDeletedPages
        );
        if (fileToUpload) {
          setSmartSplitFile(fileToUpload);
        } else {
          fileToUpload = smartSplitFile;
        }
      } catch (rotErr) {
        console.error("Error applying rotations before upload:", rotErr);
        swalError("Failed to apply page rotations to PDF. Upload aborted.");
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        return;
      }
    }

    try {
      // 0. Preflight Verification against Test Paper (first 1-5 pages)
      const ok = await preflightVerifyScan({
        file: fileToUpload,
        maxPages: 5,
        customTitle: "This file doesn't seem to match this test",
        proceedBtnText: "Upload Anyway",
        isBulk: true,
      });
      if (!ok) {
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        setSmartSplitFile(null);
        setSmartSplitOriginalFile(null);
        setSmartSplitIsCaptured(false);
        setSmartSplitRotationMode("none");
        setCbsePageRotations({});
        setLandscapePageCount(0);
        landscapePdfDocRef.current = null;
        return;
      }

      setBulkProgress("Preparing upload...");

      // 1. Reset previous scan data before starting fresh
      let resetRes = await fetchWithAuth(
        `${serverUrl}/api/smart-split/${testId}/reset`,
        { method: "POST" }
      );
      if (!resetRes || !resetRes.ok) {
        const errData = resetRes ? await resetRes.json().catch(() => ({})) : {};
        if (resetRes?.status === 409 && errData.canForce) {
          const result = await Swal.fire({
            title: "Upload Already in Progress",
            text: "A previous scan is already being processed or awaiting review for this test. Uploading a new file will replace those papers. Would you like to start over with this new file?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f09a21",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, Start Over",
            cancelButtonText: "Keep Current Job",
          });
          if (result.isConfirmed) {
            resetRes = await fetchWithAuth(
              `${serverUrl}/api/smart-split/${testId}/reset?force=true`,
              { method: "POST" }
            );
            if (!resetRes || !resetRes.ok) {
              const forceErr = resetRes ? await resetRes.json().catch(() => ({})) : {};
              throw new Error(forceErr.message || "Unable to restart the scan upload. Please try again.");
            }
          } else {
            setProcessingBulk(false);
            return;
          }
        } else {
          throw new Error(errData.message || "Unable to prepare scan upload session. Please try again.");
        }
      }

      let uploadedMasterPdfUrl = null;

      // 2. Try Direct-to-GCS via Signed URL first (zero server load, supports 800MB+)
      try {
        setBulkProgress("Preparing upload...");
        const urlRes = await fetchWithAuth(
          `${serverUrl}/api/smart-split/${testId}/request-upload-url`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename: fileToUpload?.name }),
          }
        );

        if (urlRes && urlRes.ok) {
          const { uploadUrl, masterPdfUrl } = await urlRes.json();
          if (uploadUrl) {
            setBulkProgress("Uploading papers...");
            await uploadFileWithProgress({
              url: uploadUrl,
              method: "PUT",
              file: fileToUpload,
              headers: { "Content-Type": "application/pdf" },
              isFormData: false,
              onProgress: ({ message }) => setBulkProgress(message),
            });
            uploadedMasterPdfUrl = masterPdfUrl;

            // Enqueue job with the uploaded GCS URL & chosen processingMode
            setBulkProgress("Finishing upload...");
            const enqueueRes = await fetchWithAuth(
              `${serverUrl}/api/smart-split/${testId}/enqueue`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  masterPdfUrl: uploadedMasterPdfUrl,
                  processingMode: smartSplitProcessingMode,
                  hasHeader: smartSplitHasHeader,
                }),
              }
            );
            if (!enqueueRes || !enqueueRes.ok) {
              const errData = enqueueRes ? await enqueueRes.json().catch(() => ({})) : {};
              throw new Error(errData.message || "Failed to enqueue background processing.");
            }
          }
        }
      } catch (directErr) {
        console.warn("Direct GCS upload attempt did not complete, falling back to server disk stream:", directErr);
      }

      // 3. Fallback to Express disk-streaming route (up to 1GB) if direct GCS upload didn't complete
      if (!uploadedMasterPdfUrl) {
        setBulkProgress("Uploading papers...");
        await uploadFileWithProgress({
          url: `${serverUrl}/api/smart-split/${testId}/upload-pdf`,
          method: "POST",
          file: fileToUpload,
          isFormData: true,
          extraFields: {
            processingMode: smartSplitProcessingMode,
            hasHeader: smartSplitHasHeader,
          },
          onProgress: ({ message }) => setBulkProgress(message),
        });
      }

      // 4. Branch behavior based on processingMode
      if (smartSplitProcessingMode === "batch") {
        setIsBulkModalOpen(false);
        setProcessingBulk(false);
        setBulkPhase("");
        setBulkProgress("");
        landscapeAbortRef.current = true;
        setSmartSplitFile(null);
        setSmartSplitOriginalFile(null);
        setSmartSplitIsCaptured(false);
        setSmartSplitRotationMode("none");
        setSmartSplitHasHeader(false);
        setCbsePageRotations({});
        setLandscapePageCount(0);
        landscapePdfDocRef.current = null;
        Swal.fire({
          icon: "success",
          title: "Upload Complete!",
          html: `
            <div style="text-align: left; font-size: 0.95rem; color: #374151; line-height: 1.6;">
              <p style="margin-bottom: 1rem;">
                Your answer sheets were uploaded successfully and are being split and matched in the background.
              </p>
              <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; margin-bottom: 0.5rem;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; color: #1e293b;">
                  <i class="fa-regular fa-clock" style="color: #f09a21;"></i>
                  <span><strong>Estimated Time:</strong> ~15–20 minutes for large scans</span>
                </div>
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; color: #1e293b;">
                  <i class="fa-solid fa-laptop" style="color: #16a34a;"></i>
                  <span><strong>Safe to Close:</strong> You can safely close this window and work on other tasks.</span>
                </div>
                <div style="display: flex; align-items: center; gap: 8px; color: #1e293b;">
                  <i class="fa-solid fa-list-check" style="color: #f09a21;"></i>
                  <span><strong>Where to find them:</strong> Papers will appear on the <strong>Submitted Assessments</strong> tab when ready.</span>
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonColor: "#f09a21",
          cancelButtonColor: "#64748b",
          confirmButtonText: "Go to Submitted Assessments",
          cancelButtonText: "Stay on this Page",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/instructor/test-grading/${testId}`);
          }
        });
        return;
      }

      // Online mode: keep modal open with live progress & status polling
      setBulkPhase("matching");
      setBulkProgress(testInfo?.omrTemplateId ? "Upload complete. Grading OMR sheets..." : "Upload complete. Organizing student papers...");
      pollSmartSplitStatus(0);

    } catch (error) {
      console.error("Smart Split upload error:", error);
      swalError(
        error.message || "An unexpected error occurred while uploading your scan. Please verify your connection and try again.",
        {
          title: "Upload Failed",
          showConfirmButton: true,
          confirmButtonText: "Close",
          timer: null,
        }
      );
      setProcessingBulk(false);
      setBulkPhase("");
      setBulkProgress("");
    }
  };

  // ─── Smart Split: Status polling every 4s ─────────────────────────────────
  const pollSmartSplitStatus = (initialTotalPages) => {
    if (smartSplitPollRef.current) clearInterval(smartSplitPollRef.current);

    smartSplitPollRef.current = setInterval(async () => {
      try {
        const res = await fetchWithAuth(
          `${serverUrl}/api/smart-split/${testId}/status`
        );
        if (!res || !res.ok) return;
        const data = await res.json();
        const total = data.totalPages || initialTotalPages || 0;

        if (data.status === "queued") {
          setBulkProgress(
            data.isOMRTest || testInfo?.omrTemplateId
              ? "Waiting in queue for OMR grading..."
              : "Waiting in queue..."
          );
        } else if (data.status === "processing") {
          setBulkProgress(
            data.isOMRTest || testInfo?.omrTemplateId
              ? `Grading OMR sheets (${data.processedCount || 0}/${total || "?"})...`
              : `Organizing student papers (${data.processedCount || 0}/${total || "?"})...`
          );
        }

        if (data.status === "completed" || data.status === "done") {
          clearInterval(smartSplitPollRef.current);
          smartSplitPollRef.current = null;
          setProcessingBulk(false);
          setBulkPhase("");
          setBulkProgress("");

          const papers = data.papers || [];
          const isOMR = Boolean(data.isOMRTest || testInfo?.omrTemplateId);
          const reviewCount = papers.filter((p) => !p.mappedStudentUserId || !p.mappedStudentName).length;
          const pendingCount = papers.filter((p) => !p.isSubmitted && !p.answerPaperId).length;

          // If all sheets are ready and submitted (0 in review, 0 in ready), auto-navigate to submitted assessments
          if (isOMR && papers.length > 0 && reviewCount === 0 && pendingCount === 0) {
            swalSuccess(
              `All ${papers.length} OMR answer sheet(s) have been successfully scanned, auto-graded, and submitted!`,
              {
                title: "Grading Complete",
                showConfirmButton: true,
                confirmButtonText: "View Submissions",
              }
            ).then(() => {
              navigate(`/instructor/test-grading/${testId}/submitted`);
            });
            return;
          }

          setSmartSplitResult({
            pages: data.pages,
            papers: data.papers,
            masterPdfUrl: data.masterPdfUrl,
            scanOrderOffset: data.scanOrderOffset || 0,
            enrollments: (data.enrollments?.length ? data.enrollments : students) || [],
          });
          setIsSmartSplitModalOpen(true);
        } else if (data.status === "failed") {
          clearInterval(smartSplitPollRef.current);
          smartSplitPollRef.current = null;
          setProcessingBulk(false);
          setBulkPhase("");
          setBulkProgress("");
          const safeMessage = data.errorMessage && !data.errorMessage.includes("{") && !data.errorMessage.includes("INVALID_ARGUMENT")
            ? data.errorMessage
            : "Smart Split encountered an issue while processing your scan. Please check your PDF and try uploading again.";
          swalError(
            safeMessage,
            {
              title: "Processing Failed",
              showConfirmButton: true,
              confirmButtonText: "Close",
              timer: null,
            }
          );
        }
      } catch (pollErr) {
        console.warn("[SmartSplit] Poll error:", pollErr);
      }
    }, 4000);
  };

  const handleBulkUploadSubmit = () => {
    switch (bulkUploadMethod) {
      case "qr-pdf":
        processQRPDF();
        break;
      case "qr-folder":
      case "qr-multiple":
        processQRPDFFiles();
        break;
      case "zip":
      case "folder":
      case "multiple":
        processBulkUpload();
        break;
      case "smart-split":
        handleSmartSplitUpload();
        break;
      default:
        console.error("Unknown bulk upload method:", bulkUploadMethod);
    }
  };

  const renderBulkUploadMethodSelection = () => {
    return (
      <div className="bulk-upload-methods">
        <h3>Choose Upload Method</h3>
        <div className="upload-methods-grid">
          {/* QR-Coded PDF Option */}
          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "qr-pdf" ? "selected" : ""
            }${institute?.isQRPageCountDetectionEnabled ? " ai-sheet-detection" : ""}`}
            onClick={() => setBulkUploadMethod("qr-pdf")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-qrcode"></i>
            </div>
            <h4>QR-Coded PDF</h4>
            <p>Upload a single PDF with QR codes for each student</p>
            {institute?.isQRPageCountDetectionEnabled && (
              <div className="ai-feature-tooltip">
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                AI detects page count directly from QR code Page
              </div>
            )}
          </motion.div>

          {/* Smart Split / OMR Split Option */}
          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "smart-split" ? "selected" : ""
            } ${testInfo?.omrTemplateId ? "omr-sheet-detection" : "ai-sheet-detection"}`}
            onClick={() => setBulkUploadMethod("smart-split")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className={testInfo?.omrTemplateId ? "fa-solid fa-table-cells-large" : "fa-solid fa-brain"}></i>
            </div>
            <h4>{testInfo?.omrTemplateId ? "OMR Split & Grade" : "Smart Split"}</h4>
            <p>
              {testInfo?.omrTemplateId
                ? "Upload bulk OMR scan — OMR service reads Reference IDs & grades automatically"
                : "Upload one bulk PDF — AI reads student IDs and splits automatically"}
            </p>
            <div className="ai-feature-tooltip" style={testInfo?.omrTemplateId ? { background: "#16a34a" } : undefined}>
              <i className={testInfo?.omrTemplateId ? "fa-solid fa-bolt" : "fa-solid fa-wand-magic-sparkles"}></i>
              {testInfo?.omrTemplateId ? "OMR Service (Zero AI Tokens)" : "No QR codes needed"}
            </div>
          </motion.div>
          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "qr-folder" ? "selected" : ""
            }`}
            onClick={() => setBulkUploadMethod("qr-folder")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-folder-open"></i>
              <i
                className="fa-solid fa-qrcode"
                style={{ fontSize: "0.6em", bottom: "5px", right: "5px" }}
              ></i>
            </div>
            <h4>QR Folder</h4>
            <p>Folder with PDFs containing QR codes</p>
          </motion.div>
          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "qr-multiple" ? "selected" : ""
            }`}
            onClick={() => setBulkUploadMethod("qr-multiple")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-file-circle-plus"></i>
              <i
                className="fa-solid fa-qrcode"
                style={{ fontSize: "0.6em", bottom: "5px", right: "5px" }}
              ></i>
            </div>
            <h4>QR Multiple Files</h4>
            <p>Select multiple PDFs with QR codes</p>
          </motion.div>

          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "zip" ? "selected" : ""
            }`}
            onClick={() => setBulkUploadMethod("zip")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-file-zipper"></i>
            </div>
            <h4>ZIP File</h4>
            <p>Upload a ZIP archive containing all assessments</p>
          </motion.div>

          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "folder" ? "selected" : ""
            }`}
            onClick={() => setBulkUploadMethod("folder")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-folder-open"></i>
            </div>
            <h4>Select Folder</h4>
            <p>Choose a folder with all assessment files</p>
          </motion.div>

          <motion.div
            className={`upload-method-card ${
              bulkUploadMethod === "multiple" ? "selected" : ""
            }`}
            onClick={() => setBulkUploadMethod("multiple")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="method-icon">
              <i className="fa-solid fa-file-circle-plus"></i>
            </div>
            <h4>Multiple Files</h4>
            <p>Select multiple files at once</p>
          </motion.div>
        </div>
      </div>
    );
  };

  const renderBulkUploadForm = () => {
    if (!bulkUploadMethod) {
      return renderBulkUploadMethodSelection();
    }

    return (
      <div className="bulk-upload-form">
        <button
          className="back-button"
          onClick={() => {
            setBulkUploadMethod(null);
            setSelectedFiles([]);
            setQrPdfFile(null);
            setQrRotationMode("none");
            setQrDetectedPages([]);
            landscapeAbortRef.current = true;
            setSmartSplitFile(null);
            setSmartSplitOriginalFile(null);
            setSmartSplitIsCaptured(false);
            setLandscapePageCount(0);
            setIsLandscapePreviewOpen(false);
            landscapePdfDocRef.current = null;
          }}
        >
          <i className="fa-solid fa-arrow-left"></i> Back to Methods
        </button>

        <div className="file-input-section">
          {bulkUploadMethod === "zip" && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="bulk-prefix-zip"
                  style={{ display: "block", marginBottom: 6, fontWeight: 500 }}
                >
                  Prefix (optional)
                </label>
                <input
                  id="bulk-prefix-zip"
                  type="text"
                  className="form-control"
                  placeholder="e.g. S_ (prefix before referenceId)"
                  value={bulkPrefix}
                  onChange={(e) => setBulkPrefix(e.target.value.trim())}
                  disabled={processingBulk}
                  style={{ maxWidth: 300 }}
                />
              </div>
              <label className="file-input-label">
                <i className="fa-solid fa-file-zipper"></i>
                <span>Select ZIP File</span>
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleZipFileChange}
                  disabled={processingBulk}
                />
              </label>
              <small className="helper-text">
                ZIP file should contain PDF files named as{" "}
                <strong>
                  {bulkPrefix
                    ? `${bulkPrefix}{${PDF_MATCH_KEY}}.pdf`
                    : `{${PDF_MATCH_KEY}}.pdf`}
                </strong>
              </small>
            </>
          )}

          {bulkUploadMethod === "folder" && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="bulk-prefix-folder"
                  style={{ display: "block", marginBottom: 6, fontWeight: 500 }}
                >
                  Prefix (optional)
                </label>
                <input
                  id="bulk-prefix-folder"
                  type="text"
                  className="form-control"
                  placeholder="e.g. S_ (prefix before referenceId)"
                  value={bulkPrefix}
                  onChange={(e) => setBulkPrefix(e.target.value.trim())}
                  disabled={processingBulk}
                  style={{ maxWidth: 300 }}
                />
              </div>
              <label className="file-input-label">
                <i className="fa-solid fa-folder-open"></i>
                <span>Select Folder</span>
                <input
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFolderSelect}
                  disabled={processingBulk}
                />
              </label>
              <small className="helper-text">
                Folder should contain PDF, JPG, or PNG files with filenames like{" "}
                <strong>
                  {bulkPrefix
                    ? `${bulkPrefix}{${PDF_MATCH_KEY}}.pdf`
                    : `{${PDF_MATCH_KEY}}.pdf`}
                </strong>
              </small>
            </>
          )}

          {bulkUploadMethod === "multiple" && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="bulk-prefix-multiple"
                  style={{ display: "block", marginBottom: 6, fontWeight: 500 }}
                >
                  Prefix (optional)
                </label>
                <input
                  id="bulk-prefix-multiple"
                  type="text"
                  className="form-control"
                  placeholder="e.g. S_ (prefix before referenceId)"
                  value={bulkPrefix}
                  onChange={(e) => setBulkPrefix(e.target.value.trim())}
                  disabled={processingBulk}
                  style={{ maxWidth: 300 }}
                />
              </div>
              <label className="file-input-label">
                <i className="fa-solid fa-file-circle-plus"></i>
                <span>Select Multiple Files</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleBulkFileChange}
                  disabled={processingBulk}
                />
              </label>
              <small className="helper-text">
                Select multiple PDF, JPG, or PNG files with filenames like{" "}
                <strong>
                  {bulkPrefix
                    ? `${bulkPrefix}{${PDF_MATCH_KEY}}.pdf`
                    : `{${PDF_MATCH_KEY}}.pdf`}
                </strong>
              </small>
            </>
          )}

          {bulkUploadMethod === "qr-pdf" && (
            <>
              <label className="file-input-label">
                <i className="fa-solid fa-qrcode"></i>
                <span>Select QR-Coded PDF</span>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleQRFileChange}
                  disabled={processingBulk || !librariesLoaded}
                />
              </label>
              <small className="helper-text">
                Upload a PDF where each student's section starts with the
                student QR code
              </small>
              {qrPdfFile && (
                <div style={{ marginTop: "10px", padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "0.85rem", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa-solid fa-file-pdf" style={{ color: "#16a34a" }}></i>
                  <span>Selected: <strong>{qrPdfFile.name}</strong> ({(qrPdfFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                </div>
              )}
              {!librariesLoaded && (
                <small
                  className="helper-text"
                  style={{
                    color: "#ff9800",
                    display: "block",
                    marginTop: "8px",
                  }}
                >
                  <i className="fa-solid fa-spinner fa-spin"></i> Loading QR
                  scanner libraries...
                </small>
              )}
            </>
          )}

          {bulkUploadMethod === "qr-folder" && (
            <>
              <label className="file-input-label">
                <i className="fa-solid fa-folder-open"></i>
                <span>Select Folder with QR PDFs</span>
                <input
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  accept=".pdf"
                  onChange={handleFolderSelect}
                  disabled={processingBulk || !librariesLoaded}
                />
              </label>
              <small className="helper-text">
                Select a folder containing PDF files with QR codes on the first
                page
              </small>
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: "10px", padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "0.85rem", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa-solid fa-folder-open" style={{ color: "#16a34a" }}></i>
                  <span>Selected: <strong>{selectedFiles.length}</strong> PDF {selectedFiles.length === 1 ? "file" : "files"}</span>
                </div>
              )}
              {!librariesLoaded && (
                <small
                  className="helper-text"
                  style={{
                    color: "#ff9800",
                    display: "block",
                    marginTop: "8px",
                  }}
                >
                  <i className="fa-solid fa-spinner fa-spin"></i> Loading QR
                  scanner libraries...
                </small>
              )}
            </>
          )}

          {bulkUploadMethod === "qr-multiple" && (
            <>
              <label className="file-input-label">
                <i className="fa-solid fa-file-circle-plus"></i>
                <span>Select Multiple QR PDFs</span>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleBulkFileChange}
                  disabled={processingBulk || !librariesLoaded}
                />
              </label>
              <small className="helper-text">
                Select multiple PDF files, each with a QR code on the first page
              </small>
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: "10px", padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "0.85rem", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fa-solid fa-file-circle-plus" style={{ color: "#16a34a" }}></i>
                  <span>Selected: <strong>{selectedFiles.length}</strong> PDF {selectedFiles.length === 1 ? "file" : "files"}</span>
                </div>
              )}
              {!librariesLoaded && (
                <small
                  className="helper-text"
                  style={{
                    color: "#ff9800",
                    display: "block",
                    marginTop: "8px",
                  }}
                >
                  <i className="fa-solid fa-spinner fa-spin"></i> Loading QR
                  scanner libraries...
                </small>
              )}
            </>
          )}

          {/* CBSE Alternating Rotation Option for QR Uploads */}
          {(bulkUploadMethod === "qr-pdf" ? qrPdfFile : (bulkUploadMethod === "qr-folder" || bulkUploadMethod === "qr-multiple") ? selectedFiles.length > 0 : false) && (
            <div className="smart-split-rotation-options-container" style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: "6px" }}>
                <i className="fa-solid fa-arrows-rotate" style={{ color: "var(--color-1, #f09a21)" }}></i>
                Orientation & Page Rotation Fix (Optional)
              </div>

              <div className={`landscape-rotation-fix-row ${qrRotationMode === "cbse" ? "active" : ""}`} style={{ margin: 0 }}>
                <div className="landscape-rotation-header">
                  <label className="landscape-rotation-checkbox-label">
                    <input
                      type="checkbox"
                      checked={qrRotationMode === "cbse"}
                      onChange={() => setQrRotationMode((prev) => (prev === "cbse" ? "none" : "cbse"))}
                      disabled={processingBulk || isRotatingPdf}
                    />
                    <div className="landscape-rotation-text-wrap">
                      <div className="landscape-rotation-title-row">
                        <span className="landscape-rotation-title">
                          Convert unformatted CBSE paper
                        </span>
                        <span className="cbse-badge">CBSE Board</span>
                      </div>
                      <span className="landscape-rotation-desc">
                        Auto-rotates sideways scanned answer booklets (odd 90° CW, even 270° CW) while preserving QR cover pages and backsides.
                      </span>
                    </div>
                  </label>

                  <div className="cbse-preview-transform" title="Scanned vertical booklet converted to readable landscape">
                    <div className="cbse-transform-card">
                      <span className="cbse-transform-tag unformatted">Vertical</span>
                      <div className="cbse-mini-thumb vertical">
                        <img src="/imgs/cbse-scan-vertical.png" alt="Vertical scan" />
                      </div>
                    </div>
                    <i className="fa-solid fa-arrow-right cbse-transform-arrow"></i>
                    <div className="cbse-transform-card">
                      <span className="cbse-transform-tag formatted">Landscape</span>
                      <div className="cbse-mini-thumb horizontal">
                        <img src="/imgs/cbse-scan-horizontal.png" alt="Rotated landscape" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

          {bulkUploadMethod === "smart-split" && (
            <>
              {testInfo?.omrTemplateId ? (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "12px 14px",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    color: "#166534",
                  }}
                >
                  <i className="fa-solid fa-bolt" style={{ fontSize: "1.4rem", color: "#16a34a" }}></i>
                  <div>
                    <strong style={{ fontSize: "0.9rem", display: "block" }}>Instant OMR Auto-Grading Active</strong>
                    <div style={{ fontSize: "0.78rem", color: "#15803d", marginTop: "2px" }}>
                      Enrolled students matched by reference ID are optical-scanned, graded, and scored automatically. Unmatched sheets will be flagged for review.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="smart-split-mode-selector">
                  <label className="mode-selector-heading">
                    <i className="fa-solid fa-sliders"></i> How would you like to process this file?
                  </label>
                  <div className="smart-split-modes-grid">
                    <div
                      className={`smart-split-mode-card ${smartSplitProcessingMode === "online" ? "active" : ""}`}
                      onClick={() => setSmartSplitProcessingMode("online")}
                    >
                      <div className="mode-card-badge">⚡ Quick Split</div>
                      <div className="mode-card-title">Process Now</div>
                      <p className="mode-card-desc">
                        Best for smaller files (&lt; 50 pages) · Ready in 1–2 mins · Watch progress live
                      </p>
                    </div>

                    <div
                      className={`smart-split-mode-card ${smartSplitProcessingMode === "batch" ? "active" : ""}`}
                      onClick={() => setSmartSplitProcessingMode("batch")}
                    >
                      <div className="mode-card-badge">📦 Background Split</div>
                      <div className="mode-card-title">Process in Background</div>
                      <p className="mode-card-desc">
                        Best for large files (50+ pages) · Takes ~15–20 mins · You can close and come back later
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <FileUploadSection
                disabled={processingBulk}
                isSubmitting={processingBulk}
                accept=".pdf"
                selectedFile={smartSplitFile}
                isCaptured={smartSplitIsCaptured}
                onFileChange={(file, isCaptured) => {
                  landscapeAbortRef.current = true;
                  if (file && file.size > 500 * 1024 * 1024) {
                    swalError("The selected file exceeds the 500 MB limit for Smart Split. Please choose a file smaller than 500 MB.", {
                      title: "File Too Large",
                    });
                    return;
                  }
                  setSmartSplitFile(file);
                  setSmartSplitOriginalFile(file);
                  setSmartSplitIsCaptured(isCaptured);
                  setSmartSplitRotationMode("none");
                  setCbsePageRotations({});
                  setCbseDeletedPages(new Set());
                  setLandscapePageCount(0);
                  setIsLandscapePreviewOpen(false);
                  setEnlargedPageNumber(null);
                  landscapePdfDocRef.current = null;
                }}
                onCapture={(callback) => {
                  setCaptureSource("smart-split");
                  setIsCapturing(true);
                  setIsBulkModalOpen(false);
                  window.__fileUploadCallback = callback;
                }}
                onRemoveSelectedFile={() => {
                  landscapeAbortRef.current = true;
                  setSmartSplitFile(null);
                  setSmartSplitOriginalFile(null);
                  setSmartSplitIsCaptured(false);
                  setSmartSplitRotationMode("none");
                  setCbsePageRotations({});
                  setCbseDeletedPages(new Set());
                  setLandscapePageCount(0);
                  setIsLandscapePreviewOpen(false);
                  setEnlargedPageNumber(null);
                  landscapePdfDocRef.current = null;
                }}
                onPreviewSelectedFile={(file) => {
                  const fileUrl = URL.createObjectURL(file);
                  window.open(fileUrl, "_blank");
                }}
                browseTitle="Upload from device"
                browseDescription={
                  testInfo?.omrTemplateId
                    ? "Select a bulk OMR scan PDF from your device"
                    : "Select a bulk PDF containing all student papers from your device"
                }
                browseLabel="Browse PDF"
                captureTitle="Mobile Capture"
                captureDescription={
                  testInfo?.omrTemplateId
                    ? "Scan all student OMR sheets continuously with camera into a single PDF"
                    : "Scan all students' answer papers continuously into one PDF. Smart Split will automatically separate them by roll number or student name."
                }
                captureLabel="Open Camera"
              />

              <small className="helper-text" style={{ marginTop: "12px", display: "block" }}>
                {testInfo?.omrTemplateId
                  ? "Upload a single PDF or scan all sheets continuously using Mobile Capture (Max 500 MB). The OMR service detects bubbled Reference IDs & student answers via computer vision and automatically grades each submission."
                  : "Upload a single PDF or scan all student papers continuously with Mobile Capture (Max 500 MB). Smart Split will automatically read roll numbers, IDs, or student names from each page and segment them into individual assessments."}
              </small>

              {/* Header Presence Option */}
              {!testInfo?.omrTemplateId && (
                <div
                  className={`landscape-rotation-fix-row ${smartSplitHasHeader ? "active" : ""}`}
                  style={{
                    marginTop: "14px",
                    marginBottom: "0",
                    borderColor: smartSplitHasHeader ? "var(--color-1, #f09a21)" : "#e2e8f0",
                    background: smartSplitHasHeader ? "#fffcf7" : "#ffffff",
                    boxShadow: smartSplitHasHeader ? "0 2px 8px rgba(240, 154, 33, 0.12)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
                  }}
                >
                  <div className="landscape-rotation-header" style={{ alignItems: "center" }}>
                    <label className="landscape-rotation-checkbox-label" style={{ cursor: "pointer", margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={smartSplitHasHeader}
                        onChange={(e) => setSmartSplitHasHeader(e.target.checked)}
                        disabled={processingBulk}
                        style={{
                          width: "20px",
                          height: "20px",
                          marginTop: "2px",
                          accentColor: "var(--color-1, #f09a21)",
                        }}
                      />
                      <div className="landscape-rotation-text-wrap">
                        <div className="landscape-rotation-title-row">
                          <span className="landscape-rotation-title">
                            Does each student's first page have a printed header on top?
                          </span>
                          <span
                            className="cbse-badge"
                            style={{
                              background: "rgba(37, 99, 235, 0.1)",
                              color: "#2563eb",
                              borderColor: "rgba(37, 99, 235, 0.25)",
                            }}
                          >
                            Recommended for School Exams
                          </span>
                        </div>
                        <span className="landscape-rotation-desc">
                          Check this if page 1 of each answer booklet has a printed box for School Name, Student Name, Roll No., etc.
                        </span>
                      </div>
                    </label>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setIsHeaderPreviewOpen(true)}
                        style={{
                          background: "#f8fafc",
                          color: "#2563eb",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          padding: "6px 12px",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.15s ease",
                          whiteSpace: "nowrap",
                        }}
                        title="Click to view an example of how the header looks"
                      >
                        <i className="fa-solid fa-eye"></i>
                        View Example Header
                      </button>

                      <div
                        onClick={() => setIsHeaderPreviewOpen(true)}
                        style={{
                          cursor: "pointer",
                          borderRadius: "6px",
                          overflow: "hidden",
                          border: "1px solid #cbd5e1",
                          width: "48px",
                          height: "38px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#f8fafc",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                          flexShrink: 0,
                          transition: "transform 0.15s ease",
                        }}
                        title="Click to zoom into example header"
                      >
                        <img
                          src="/imgs/header/header.png"
                          alt="Sample header"
                          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {smartSplitFile && (
                <div className="smart-split-rotation-options-container" style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#475569", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa-solid fa-arrows-rotate" style={{ color: "var(--color-1, #f09a21)" }}></i>
                    Orientation & Page Rotation Fix (Optional)
                  </div>

                  {/* CBSE Alternating Rotation Option (Odd 90° CW, Even 270° CW) */}
                  <div className={`landscape-rotation-fix-row ${smartSplitRotationMode === "cbse" ? "active" : ""}`} style={{ margin: 0 }}>
                    <div className="landscape-rotation-header">
                      <label className="landscape-rotation-checkbox-label">
                        <input
                          type="checkbox"
                          checked={smartSplitRotationMode === "cbse"}
                          onChange={handleToggleCbseRotation}
                          disabled={processingBulk || isRotatingPdf}
                        />
                        <div className="landscape-rotation-text-wrap">
                          <div className="landscape-rotation-title-row">
                            <span className="landscape-rotation-title">
                              Convert unformatted CBSE paper
                            </span>
                            <span className="cbse-badge">CBSE Board</span>
                          </div>
                          <span className="landscape-rotation-desc">
                            Auto-rotates sideways scanned answer booklets (odd 90° CW, even 270° CW) into readable landscape. Preview & adjust any page before splitting.
                          </span>
                        </div>
                      </label>

                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        {smartSplitRotationMode === "cbse" && (
                          <button
                            type="button"
                            className="cbse-preview-open-btn"
                            style={{
                              background: "#f8fafc",
                              color: "#2563eb",
                              border: "1px solid #cbd5e1",
                              borderRadius: "6px",
                              padding: "6px 12px",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "all 0.15s ease",
                            }}
                            onClick={() => setIsLandscapePreviewOpen(true)}
                            disabled={processingBulk || isRotatingPdf}
                          >
                            <i className="fa-solid fa-eye"></i>
                            Preview & Adjust
                          </button>
                        )}

                        <div className="cbse-preview-transform" title="Scanned vertical booklet converted to readable landscape">
                          <div className="cbse-transform-card">
                            <span className="cbse-transform-tag unformatted">Vertical</span>
                            <div className="cbse-mini-thumb vertical">
                              <img src="/imgs/cbse-scan-vertical.png" alt="Vertical scan" />
                            </div>
                          </div>
                          <div className="cbse-transform-arrow">
                            <i className="fa-solid fa-arrow-right"></i>
                          </div>
                          <div className="cbse-transform-card">
                            <span className="cbse-transform-tag formatted">Landscape</span>
                            <div className="cbse-mini-thumb horizontal">
                              <img src="/imgs/cbse-scan-horizontal.png" alt="Landscape scan" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isRotatingPdf && (
                    <span className="landscape-rotating-spinner">
                      <i className="fa-solid fa-spinner fa-spin"></i> Rotating PDF & generating preview...
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Prefix is provided at the top (above method selection). Removed duplicate input here. */}

        {selectedFiles.length > 0 && (
          <div className="selected-files-info">
            <p>
              <strong>{selectedFiles.length}</strong> file(s) selected
            </p>
          </div>
        )}
        {/* Show selected QR PDF file name */}
        {bulkUploadMethod === "qr-pdf" && qrPdfFile && (
          <div className="selected-files-info">
            <p>
              <strong>{qrPdfFile.name}</strong> selected
            </p>
          </div>
        )}

        {processingBulk && bulkPhase !== "completed" ? (
          <div className="bulk-progress-container">
            <div
              className="progress-indicator"
              style={{
                background:
                  bulkPhase === "preparing" || bulkPhase === "uploading"
                    ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                    : bulkPhase === "extracting"
                      ? "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)"
                      : bulkPhase === "matching"
                        ? "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)"
                        : bulkPhase === "completed"
                          ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                          : "#f8f9fa",
                border: `2px solid ${
                  bulkPhase === "preparing" || bulkPhase === "uploading"
                    ? "#4caf50"
                    : bulkPhase === "extracting"
                      ? "#ff9800"
                      : bulkPhase === "matching"
                        ? "#9c27b0"
                        : bulkPhase === "completed"
                          ? "#2e7d32"
                          : "#e0e0e0"
                }`,
              }}
            >
              {bulkPhase !== "completed" ? (
                <Loader
                  fontSize="50px"
                  style={{
                    left: "0",
                    color:
                      bulkPhase === "preparing" || bulkPhase === "uploading"
                        ? "#388e3c"
                        : bulkPhase === "extracting"
                          ? "#f57c00"
                          : bulkPhase === "matching"
                            ? "#7b1fa2"
                            : "#666",
                  }}
                />
              ) : (
                <div className="success-icon">
                  <i className="fa-solid fa-check-circle"></i>
                </div>
              )}

              <div className="progress-text">
                <div className="progress-title">{bulkProgress}</div>

                {currentStudentProcessing && bulkPhase === "uploading" && (
                  <div className="current-student">
                    <i className="fa-solid fa-user"></i>
                    {currentStudentProcessing}
                  </div>
                )}

                <div className="progress-description">
                  {bulkPhase === "preparing" && "Preparing files for upload..."}
                  {/*For extracting and matching, different progress text for QR PDF and ZIP files*/}
                  {bulkPhase === "extracting" &&
                    (bulkUploadMethod === "qr-pdf"
                      ? "Reading PDF pages..."
                      : bulkUploadMethod === "smart-split"
                        ? "Rendering PDF pages..."
                        : "Extracting files from ZIP archive...")}
                  {bulkPhase === "matching" &&
                    (bulkUploadMethod === "qr-pdf"
                      ? "Scanning for QR codes and matching students..."
                      : bulkUploadMethod === "smart-split"
                        ? (testInfo?.omrTemplateId
                            ? "Please wait until OMR sheets are successfully graded..."
                            : "Please wait until student papers are successfully organized...")
                        : "Matching files with student records...")}
                  {bulkPhase === "uploading" &&
                    (bulkUploadMethod === "smart-split"
                      ? "Uploading answer sheets..."
                      : "Uploading assessments to server...")}
                  {bulkPhase === "completed" &&
                    "All files processed successfully!"}
                </div>
              </div>
              {bulkPhase !== "completed" && bulkUploadMethod !== "smart-split" && (
                <button
                  className="btn-submit"
                  onClick={abortUpload}
                  style={{
                    marginTop: "20px",
                    backgroundColor: "#f44336",
                    borderColor: "#f44336",
                  }}
                >
                  <i className="fa-solid fa-xmark"></i> Cancel Upload
                </button>
              )}
              <div ref={bulkProgressEndRef} />
            </div>
          </div>
        ) : (
          <button
            onClick={handleBulkUploadSubmit}
            className="btn-submit"
            disabled={
              bulkUploadMethod === "qr-pdf"
                ? (!qrPdfFile && selectedFiles.length === 0) || !librariesLoaded
                : bulkUploadMethod === "smart-split"
                  ? !smartSplitFile
                  : selectedFiles.length === 0
            }
          >
            <i className="fa-solid fa-upload"></i> Start Bulk Upload
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {isCapturing ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
            mode={captureSource === "smart-split" ? "bulk" : "answer"}
            testId={testId}
            studentId={
              captureSource === "smart-split"
                ? null
                : selectedStudent?.studentId
            }
            studentName={
              captureSource === "smart-split"
                ? testInfo?.testName
                  ? `${testInfo.testName} (All Students)`
                  : "Bulk Scan (All Students)"
                : `${selectedStudent?.firstName} ${selectedStudent?.lastName}`
            }
          />
        </div>
      ) : (
        <motion.div
          className="pending-assessment-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {loading && <Loader fontSize="50px" />}

          <div className="action-buttons">
            <motion.button
              className="btn-upload-bulk"
              onClick={() => setIsBulkModalOpen(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <i className="fa-solid fa-upload"></i> Bulk Upload
            </motion.button>
          </div>
          <Table
            data={pendingData}
            columns={columns}
            caption="Pending Submissions"
            textAlign="center"
          />

          {/* Single Upload Modal */}
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <div className="modal-body">
              <h2>Upload Assessment for</h2>
              {renderAnswerPaperUpload()}
              {isSubmitting ? (
                <Loader style={{ fontSize: "40px" }} />
              ) : (
                <button
                  onClick={handleUpload}
                  className="btn-submit"
                  disabled={
                    !selectedStudent?.answerPaper &&
                    !selectedStudent?.capturedFile
                  }
                >
                  <i className="fa-solid fa-upload"></i> Submit
                </button>
              )}
            </div>
          </Modal>

          {/* Bulk Upload Modal */}
          <Modal
            isOpen={isBulkModalOpen}
            closeButton={!(processingBulk && bulkUploadMethod === "smart-split")}
            onClose={() => {
              if (processingBulk && bulkUploadMethod === "smart-split") {
                return; // Cannot cancel or close modal while online smart-split is processing
              }
              if (processingBulk) {
                abortUpload();
              }
              setIsBulkModalOpen(false);
              setSelectedFiles([]);
              setBulkUploadMethod(null);
              setQrPdfFile(null);
              setQrRotationMode("none");
              setQrDetectedPages([]);
              landscapeAbortRef.current = true;
              setSmartSplitFile(null);
              setSmartSplitOriginalFile(null);
              setSmartSplitIsCaptured(false);
              setSmartSplitRotationMode("none");
              setSmartSplitHasHeader(false);
              setIsHeaderPreviewOpen(false);
              setCbsePageRotations({});
              setCbseDeletedPages(new Set());
              setLandscapePageCount(0);
              setIsLandscapePreviewOpen(false);
              setEnlargedPageNumber(null);
              landscapePdfDocRef.current = null;
              setBulkPhase("");
              setBulkProgress("");
              setCurrentStudentProcessing("");
            }}
            maxWidth="800px"
          >
            <div className="bulk-upload-modal">
              <h2>
                <i className="fa-solid fa-cloud-arrow-up"></i> Bulk Upload
                Assessments
              </h2>
              {renderBulkUploadForm()}
            </div>
          </Modal>

          {/* Landscape Scan Preview Modal (one-time, canvas-based, no caching) */}
          <Modal
            isOpen={isLandscapePreviewOpen}
            onClose={() => {
              setIsLandscapePreviewOpen(false);
              setEnlargedPageNumber(null);
            }}
            maxWidth="1200px"
            padding="0"
            zIndex={1200}
            closeButton={false}
          >
            <div className="landscape-preview-popup-body">
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                    <i className="fa-solid fa-arrows-rotate" style={{ color: "var(--color-1, #f09a21)" }}></i>
                    CBSE Alternating Rotation Preview
                    <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "#64748b" }}>
                      ({landscapePageCount - cbseDeletedPages.size} {landscapePageCount - cbseDeletedPages.size === 1 ? "page" : "pages"}
                      {cbseDeletedPages.size > 0 && ` · ${cbseDeletedPages.size} deleted`})
                    </span>
                  </h3>
                  <p style={{ margin: "4px 0 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                    {bulkUploadMethod === "qr-pdf"
                      ? "QR cover sheets and backsides are kept unrotated (0°). Odd answer booklet pages rotate 90° CW, even answer pages rotate 270° CW."
                      : "Odd pages rotate 90° CW, even pages rotate 270° CW. You can rotate individual pages or delete blank/unwanted sheets."}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {cbseDeletedPages.size > 0 && (
                    <button
                      type="button"
                      style={{
                        background: "#fff1f2",
                        color: "#e11d48",
                        border: "1px solid #fecdd3",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.15s ease",
                      }}
                      onClick={handleRestoreDeletedPages}
                      title="Restore all deleted pages back to the document"
                    >
                      <i className="fa-solid fa-rotate-left"></i>
                      Restore Deleted ({cbseDeletedPages.size})
                    </button>
                  )}
                  <button
                    type="button"
                    style={{
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onClick={handleResetCbseRotations}
                    title="Reset all pages to standard CBSE alternating rotation and restore deleted pages"
                  >
                    <i className="fa-solid fa-arrow-rotate-left"></i>
                    Reset to CBSE Default
                  </button>
                  <button
                    type="button"
                    style={{
                      background: "transparent",
                      border: "none",
                      fontSize: "1.2rem",
                      color: "#64748b",
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}
                    onClick={() => setIsLandscapePreviewOpen(false)}
                    aria-label="Close"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </div>

              <LandscapeVirtualGrid
                pdfDocRef={landscapePdfDocRef}
                pageCount={landscapePageCount}
                deletedPages={cbseDeletedPages}
                pageRotations={cbsePageRotations}
                qrDetectedPages={qrDetectedPages}
                onRotatePage={handleRotateIndividualPage}
                onDeletePage={handleDeletePage}
                onExpandPage={(pn) => setEnlargedPageNumber(pn)}
              />

              <div className="landscape-preview-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", borderTop: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
                  <i className="fa-solid fa-circle-info" style={{ marginRight: "6px", color: "#f09a21" }}></i>
                  {bulkUploadMethod === "qr-pdf"
                    ? "Rotations shown here will be applied to the master PDF and extracted for each student."
                    : cbseDeletedPages.size > 0
                    ? `${cbseDeletedPages.size} page(s) will be deleted. All rotations and deletions will be applied when Smart Split processes the document.`
                    : "All individual rotations applied here will be saved and used when Smart Split processes the document."}
                </span>
                <button
                  type="button"
                  className="landscape-apply-changes-btn"
                  onClick={handleConfirmLandscapePreview}
                >
                  <i className="fa-solid fa-check" style={{ marginRight: "6px" }}></i>
                  Confirm & Apply Changes
                </button>
              </div>
            </div>
          </Modal>

          {/* Example Header Preview Modal */}
          {isHeaderPreviewOpen && (
            <Modal
              isOpen={isHeaderPreviewOpen}
              onClose={() => setIsHeaderPreviewOpen(false)}
              maxWidth="640px"
              zIndex={1350}
              closeButton={false}
            >
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        background: "#eff6ff",
                        color: "#2563eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.1rem",
                      }}
                    >
                      <i className="fa-solid fa-file-invoice"></i>
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#0f172a", fontWeight: 700 }}>
                        Sample Answer Sheet Header
                      </h3>
                      <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
                        Example of a printed header on the first page
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsHeaderPreviewOpen(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.2rem",
                      color: "#64748b",
                      cursor: "pointer",
                      padding: "4px 8px",
                      borderRadius: "6px",
                    }}
                    title="Close preview"
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>

                {/* Helpful Explanation */}
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    fontSize: "0.83rem",
                    color: "#166534",
                    lineHeight: 1.45,
                  }}
                >
                  <i className="fa-solid fa-circle-check" style={{ color: "#16a34a", marginTop: "2px", fontSize: "1rem" }}></i>
                  <div>
                    <strong>Notice the top printed header:</strong> It includes the <em>School Name, Student Name, Roll No., Section, and Date</em>. When this option is checked, the AI looks specifically for this header box to separate student answer sheets cleanly.
                  </div>
                </div>

                {/* Scrollable image container */}
                <div
                  style={{
                    border: "1.5px solid #cbd5e1",
                    borderRadius: "10px",
                    overflow: "hidden",
                    maxHeight: "440px",
                    overflowY: "auto",
                    background: "#f8fafc",
                    display: "flex",
                    justifyContent: "center",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  <img
                    src="/imgs/header/header.png"
                    alt="Sample printed header on answer sheet"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </div>

                {/* Footer Buttons */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSmartSplitHasHeader(true);
                      setIsHeaderPreviewOpen(false);
                    }}
                    style={{
                      background: "var(--color-1, #f09a21)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 1px 3px rgba(240, 154, 33, 0.3)",
                    }}
                  >
                    <i className="fa-solid fa-check"></i> Yes, My Papers Have This Header
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsHeaderPreviewOpen(false)}
                    style={{
                      background: "#f1f5f9",
                      color: "#475569",
                      border: "1px solid #cbd5e1",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Enlarged Page Preview Modal (Z-Index 1300 above CBSE preview modal) */}
          {enlargedPageNumber !== null && (
            <Modal
              isOpen={enlargedPageNumber !== null}
              onClose={() => setEnlargedPageNumber(null)}
              maxWidth="1050px"
              modalContentClass="enlarged-page-modal-content"
              padding="0"
              zIndex={1300}
              closeButton={false}
            >
              {(() => {
                const nonDeletedPages = Array.from({ length: landscapePageCount }, (_, idx) => idx + 1).filter(
                  (p) => !cbseDeletedPages.has(p)
                );
                const currentIndex = nonDeletedPages.indexOf(enlargedPageNumber);
                const currentDisplayNum = currentIndex !== -1 ? currentIndex + 1 : enlargedPageNumber;
                const totalDisplayPages = nonDeletedPages.length;
                const currentRotation =
                  cbsePageRotations[enlargedPageNumber] !== undefined
                    ? cbsePageRotations[enlargedPageNumber]
                    : (enlargedPageNumber % 2 !== 0 ? 90 : 270);

                const hasPrev = currentIndex > 0;
                const hasNext = currentIndex < nonDeletedPages.length - 1;

                return (
                  <div
                    className="enlarged-page-popup-body"
                    style={{
                      background: "#ffffff",
                      borderRadius: "16px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      maxHeight: "100%",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        flexShrink: 0,
                        padding: "10px 18px",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#ffffff",
                        flexWrap: "nowrap",
                        gap: "10px",
                      }}
                    >
                      {/* Left: Page metadata & rotation badge */}
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "#eff6ff",
                            border: "1px solid #dbeafe",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#2563eb",
                            fontSize: "0.95rem",
                          }}
                        >
                          <i className="fa-solid fa-file-lines" />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <h3 style={{ margin: 0, fontSize: "1.02rem", fontWeight: 700, color: "#1e293b" }}>
                              Page #{currentDisplayNum}
                            </h3>
                            {currentDisplayNum !== enlargedPageNumber && (
                              <span
                                style={{
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  background: "#f1f5f9",
                                  border: "1px solid #e2e8f0",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                                title={`Original page #${enlargedPageNumber} before deletions`}
                              >
                                Orig #{enlargedPageNumber}
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#2563eb",
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                padding: "2px 8px",
                                borderRadius: "12px",
                              }}
                            >
                              {currentRotation % 360}° Rotation
                            </span>
                          </div>
                          <span style={{ fontSize: "0.76rem", color: "#64748b" }}>
                            Viewing sheet {currentDisplayNum} of {totalDisplayPages} in scanned booklet
                          </span>
                        </div>
                      </div>

                      {/* Center: Page navigation (Prev / Next) */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "8px",
                          padding: "3px 6px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => hasPrev && setEnlargedPageNumber(nonDeletedPages[currentIndex - 1])}
                          disabled={!hasPrev}
                          style={{
                            background: hasPrev ? "#f1f5f9" : "transparent",
                            border: "none",
                            borderRadius: "6px",
                            padding: "4px 10px",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: hasPrev ? "#1e293b" : "#cbd5e1",
                            cursor: hasPrev ? "pointer" : "not-allowed",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            transition: "all 0.15s ease",
                          }}
                          title="Previous page (Left Arrow)"
                        >
                          <i className="fa-solid fa-chevron-left" />
                          <span>Prev</span>
                        </button>

                        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155", minWidth: "55px", textAlign: "center" }}>
                          {currentDisplayNum} / {totalDisplayPages}
                        </span>

                        <button
                          type="button"
                          onClick={() => hasNext && setEnlargedPageNumber(nonDeletedPages[currentIndex + 1])}
                          disabled={!hasNext}
                          style={{
                            background: hasNext ? "#f1f5f9" : "transparent",
                            border: "none",
                            borderRadius: "6px",
                            padding: "4px 10px",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: hasNext ? "#1e293b" : "#cbd5e1",
                            cursor: hasNext ? "pointer" : "not-allowed",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            transition: "all 0.15s ease",
                          }}
                          title="Next page (Right Arrow)"
                        >
                          <span>Next</span>
                          <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>

                      {/* Right: Actions (Rotate, Delete, Close) */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <button
                          type="button"
                          onClick={() => handleRotateIndividualPage(enlargedPageNumber)}
                          style={{
                            background: "#eff6ff",
                            color: "#2563eb",
                            border: "1px solid #bfdbfe",
                            borderRadius: "7px",
                            padding: "6px 12px",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.15s ease",
                          }}
                          title="Rotate this page 90° clockwise"
                        >
                          <i className="fa-solid fa-rotate-right" />
                          Rotate 90°
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeletePage(enlargedPageNumber)}
                          style={{
                            background: "#fff1f2",
                            color: "#e11d48",
                            border: "1px solid #fecdd3",
                            borderRadius: "7px",
                            padding: "6px 12px",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.15s ease",
                          }}
                          title="Delete this page from document"
                        >
                          <i className="fa-solid fa-trash-can" />
                          Delete
                        </button>

                        <button
                          type="button"
                          onClick={() => setEnlargedPageNumber(null)}
                          style={{
                            background: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "7px",
                            width: "32px",
                            height: "32px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#64748b",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                          title="Close enlarged preview (Esc)"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>

                    {/* Canvas Area */}
                    <EnlargedPageCanvas
                      pdfDocRef={landscapePdfDocRef}
                      pageNumber={enlargedPageNumber}
                      rotation={currentRotation}
                    />

                    {/* Footer bar with shortcut hint and return button */}
                    <div
                      style={{
                        flexShrink: 0,
                        padding: "10px 20px",
                        borderTop: "1px solid #e2e8f0",
                        background: "#ffffff",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: "#64748b",
                        fontSize: "0.8rem",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span>
                          <kbd style={{ background: "#f8fafc", padding: "2px 6px", borderRadius: "4px", border: "1px solid #cbd5e1", color: "#334155", fontSize: "0.75rem", fontWeight: 600 }}>←</kbd>
                          {" / "}
                          <kbd style={{ background: "#f8fafc", padding: "2px 6px", borderRadius: "4px", border: "1px solid #cbd5e1", color: "#334155", fontSize: "0.75rem", fontWeight: 600 }}>→</kbd>
                          {" Navigate"}
                        </span>
                        <span>•</span>
                        <span>
                          <kbd style={{ background: "#f8fafc", padding: "2px 6px", borderRadius: "4px", border: "1px solid #cbd5e1", color: "#334155", fontSize: "0.75rem", fontWeight: 600 }}>Esc</kbd>
                          {" Close"}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEnlargedPageNumber(null)}
                        style={{
                          background: "#f1f5f9",
                          border: "1px solid #cbd5e1",
                          borderRadius: "6px",
                          color: "#334155",
                          padding: "5px 14px",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        Back to Grid
                      </button>
                    </div>
                  </div>
                );
              })()}
            </Modal>
          )}

          {/* QR Summary Confirmation Modal */}
          <Modal
            isOpen={isQrSummaryModalOpen}
            onClose={() => {
              if (processingBulk) {
                abortUpload();
              }
              setIsQrSummaryModalOpen(false);
              setQrSummaryData([]);
              setQrSummaryFile(null);
              setSkippedStudents(new Set());
              setBulkPhase("");
              setBulkProgress("");
              setCurrentStudentProcessing("");
              setQrPdfFile(null);
              setSelectedFiles([]);
              setQrRotationMode("none");
              setQrDetectedPages([]);
              setCbsePageRotations({});
              setCbseDeletedPages(new Set());
              setLandscapePageCount(0);
              setIsLandscapePreviewOpen(false);
              if (landscapePdfDocRef.current) {
                try {
                  landscapePdfDocRef.current.destroy();
                } catch (_) {}
                landscapePdfDocRef.current = null;
              }
            }}
            maxWidth="95%"
          >
            <div className="summary-modal">
              <h2>
                {processingBulk ? (
                  <>
                    <i className="fa-solid fa-cloud-arrow-up"></i> Uploading
                    Assessments
                  </>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <i
                        className="fa-solid fa-list-check"
                        style={{ color: "var(--color-1)" }}
                      ></i>{" "}
                      Review Detected Students
                    </div>
                    {qrRotationMode === "cbse" && bulkUploadMethod === "qr-pdf" && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span className="cbse-badge" style={{ fontSize: "0.75rem", padding: "4px 8px" }}>
                          <i className="fa-solid fa-arrows-rotate" style={{ marginRight: "4px" }}></i>
                          CBSE Rotations Active
                        </span>
                        <button
                          type="button"
                          className="cbse-preview-open-btn"
                          style={{
                            background: "#f8fafc",
                            color: "#2563eb",
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px",
                            padding: "6px 12px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.15s ease",
                          }}
                          onClick={handleOpenQrLandscapePreview}
                          disabled={processingBulk || isRotatingPdf}
                        >
                          <i className="fa-solid fa-eye"></i>
                          Preview Rotated Pages
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </h2>
              <div className="summary-content">
                {processingBulk ? (
                  <div className="bulk-progress-container">
                    <div
                      className="progress-indicator"
                      style={{
                        background:
                          bulkPhase === "uploading" || bulkPhase === "preparing"
                            ? "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)"
                            : bulkPhase === "completed"
                              ? "linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)"
                              : "#f8f9fa",
                        border: `2px solid ${
                          bulkPhase === "uploading" || bulkPhase === "preparing"
                            ? "#4caf50"
                            : bulkPhase === "completed"
                              ? "#2e7d32"
                              : "#e0e0e0"
                        }`,
                      }}
                    >
                      {bulkPhase !== "completed" ? (
                        <Loader
                          fontSize="50px"
                          style={{
                            left: "0",
                            color:
                              bulkPhase === "uploading" ||
                              bulkPhase === "preparing"
                                ? "#388e3c"
                                : "#666",
                          }}
                        />
                      ) : (
                        <div className="success-icon">
                          <i className="fa-solid fa-check-circle"></i>
                        </div>
                      )}

                      <div className="progress-text">
                        <div className="progress-title">{bulkProgress}</div>
                        {currentStudentProcessing &&
                          (bulkPhase === "uploading" ||
                            bulkPhase === "preparing") && (
                            <div className="current-student">
                              <i className="fa-solid fa-user"></i>
                              {currentStudentProcessing}
                            </div>
                          )}
                        <div className="progress-description">
                          {(bulkPhase === "uploading" ||
                            bulkPhase === "preparing") &&
                            "Uploading PDFs..."}
                          {bulkPhase === "completed" &&
                            "Upload Process Completed!"}
                        </div>
                      </div>
                      <div ref={bulkProgressEndRef} />
                    </div>
                  </div>
                ) : (
                  <>
                    {/* --- Summary Stats --- */}
                    {(() => {
                      const detectedIds = new Set(
                        qrSummaryData.map((d) => d.studentId),
                      );
                      const missingCount = students.filter(
                        (s) => !detectedIds.has(s._id),
                      ).length;
                      const unrecognizedCount = qrSummaryData.filter(
                        (d) => !d.isRecognized,
                      ).length;
                      return (
                        <div className="qr-summary-stats">
                          <div className="qr-stat-card qr-stat-found">
                            <i className="fa-solid fa-check-circle"></i>
                            <span className="qr-stat-value">
                              {
                                qrSummaryData.filter((d) => d.isRecognized)
                                  .length
                              }
                            </span>
                            <span className="qr-stat-label">Matched</span>
                          </div>

                          <div className="qr-stat-card qr-stat-notfound">
                            <i className="fa-solid fa-user-xmark"></i>
                            <span className="qr-stat-value">
                              {missingCount}
                            </span>
                            <span className="qr-stat-label">Not Found</span>
                          </div>

                          <div className="qr-stat-card qr-stat-unrecognized">
                            <i className="fa-solid fa-question-circle"></i>
                            <span className="qr-stat-value">
                              {unrecognizedCount}
                            </span>
                            <span className="qr-stat-label">Unrecognized</span>
                          </div>
                          <div className="qr-stat-card qr-stat-upload">
                            <i className="fa-solid fa-cloud-arrow-up"></i>
                            <span className="qr-stat-value">
                              {
                                qrSummaryData.filter(
                                  (d) =>
                                    d.isRecognized &&
                                    !skippedStudents.has(d.studentId),
                                ).length
                              }
                            </span>
                            <span className="qr-stat-label">To Upload</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* --- Student Summary Table --- */}
                    <div className="summary-table-container">
                      <table className="summary-table">
                        <thead>
                          <tr>
                            <th>Student Name</th>
                            <th>Reference ID</th>
                            {institute?.isQRPageCountDetectionEnabled && (
                              <th style={{ textAlign: "center" }}>Number of Pages 
                              (Student Entered)</th>
                            )}
                            <th
                              style={{
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              Number of Pages (Scanned)
                            </th>
                            <th style={{ textAlign: "center" }}>Page Range</th>
                            <th style={{ textAlign: "center" }}>Skip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* --- Not Found Students (at top, light red) --- */}
                          {(() => {
                            const detectedIds = new Set(
                              qrSummaryData.map((d) => d.studentId),
                            );
                            return students
                              .filter((s) => !detectedIds.has(s._id))
                              .map((s, idx) => (
                                <tr
                                  key={`missing-${idx}`}
                                  className="not-found-row"
                                >
                                  <td>
                                    <i
                                      className="fa-solid fa-user-xmark"
                                      style={{
                                        marginRight: "6px",
                                        fontSize: "0.8rem",
                                      }}
                                    ></i>
                                    {s.firstName} {s.lastName}
                                  </td>
                                  <td
                                    style={{
                                      color: "#a0aec0",
                                    }}
                                  >
                                    {s.referenceId}
                                  </td>
                                  {institute?.isQRPageCountDetectionEnabled && (
                                    <td
                                      style={{
                                        textAlign: "center",
                                        color: "#a0aec0",
                                      }}
                                    >
                                      —
                                    </td>
                                  )}
                                  <td style={{ textAlign: "center" }}>
                                    <span
                                      style={{
                                        textAlign: "center",
                                      }}
                                    >
                                      —
                                    </span>
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "center",
                                      color: "#a0aec0",
                                    }}
                                  >
                                    —
                                  </td>
                                  <td
                                    style={{
                                      textAlign: "center",
                                      color: "#a0aec0",
                                    }}
                                  >
                                    —
                                  </td>
                                </tr>
                              ));
                          })()}
                          {/* --- Detected Students --- */}
                          {qrSummaryData.map((data, idx) => {
                            const isSkipped = skippedStudents.has(
                              data.studentId,
                            );
                            return (
                              <tr
                                key={idx}
                                className={`${data.isRecognized ? "recognized-row" : "unrecognized-row"}${isSkipped ? " skipped-row" : ""}`}
                              >
                                <td>
                                  {data.fullName}
                                  {qrRotationMode === "cbse" && (
                                    <span
                                      className="cbse-badge"
                                      style={{
                                        marginLeft: "8px",
                                        fontSize: "0.65rem",
                                        padding: "1px 5px",
                                        verticalAlign: "middle",
                                      }}
                                      title="Pages rotated for CBSE booklet alignment"
                                    >
                                      CBSE Rotated
                                    </span>
                                  )}
                                </td>
                                <td>{data.referenceId}</td>
                                {institute?.isQRPageCountDetectionEnabled && (
                                  <td style={{ textAlign: "center" }}>
                                    {data.numberOfPages || "—"}
                                  </td>
                                )}
                                <td style={{ textAlign: "center" }}>
                                  <span style={{ fontWeight: "bold" }}>
                                    {data.pageCount}
                                  </span>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {data.pageRange}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {data.isRecognized && (() => {
                                    const isInPending = pendingData.some(
                                      (s) => s.studentId === data.studentId,
                                    );
                                    const isAutoSkipped =
                                      !isInPending ||
                                      (institute?.isQRPageCountDetectionEnabled &&
                                        data.numberOfPages != null &&
                                        !isNaN(data.numberOfPages) &&
                                        data.numberOfPages !== 0 &&
                                        data.pageCount !== data.numberOfPages);
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        <label className="skip-toggle">
                                          <input
                                            type="checkbox"
                                            checked={isSkipped || !isInPending}
                                            disabled={!isInPending}
                                            onChange={() => {
                                              if (!isInPending) return;
                                              setSkippedStudents((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(data.studentId)) {
                                                  next.delete(data.studentId);
                                                } else {
                                                  next.add(data.studentId);
                                                }
                                                return next;
                                              });
                                            }}
                                          />
                                          <span className="skip-toggle-slider"></span>
                                        </label>
                                        {!isInPending && (
                                          <span
                                            title="Assessment already uploaded for this student"
                                            style={{
                                              fontSize: "0.65rem",
                                              color: "#e53e3e",
                                              fontWeight: 600,
                                              cursor: "help",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            ⚠ Assessment Already Uploaded
                                          </span>
                                        )}
                                        {isInPending && isAutoSkipped && (
                                          <span
                                            title={`${data.pageCount > data.numberOfPages ? "Extra" : "Missing"} ${Math.abs(data.pageCount - data.numberOfPages)} Pages Detected`}
                                            style={{
                                              fontSize: "0.65rem",
                                              color: "#e53e3e",
                                              fontWeight: 600,
                                              cursor: "help",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            ⚠ {data.pageCount > data.numberOfPages ? "Extra" : "Less"} {Math.abs(data.pageCount - data.numberOfPages)} Pages
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    if (processingBulk) {
                      if (abortControllerRef.current) {
                        abortControllerRef.current.abort();
                      }
                      setBulkPhase("");
                      setBulkProgress("Upload stopped by user.");
                      setCurrentStudentProcessing("");

                      setTimeout(() => {
                        setIsQrSummaryModalOpen(false);
                        setQrSummaryData([]);
                        setQrSummaryFile(null);
                        setQrPdfFile(null);
                        setQrRotationMode("none");
                        setQrDetectedPages([]);
                        setProcessingBulk(false);
                      }, 1500);
                    } else {
                      setIsQrSummaryModalOpen(false);
                      setQrSummaryData([]);
                      setQrSummaryFile(null);
                      setQrRotationMode("none");
                      setQrDetectedPages([]);
                    }
                  }}
                  disabled={bulkProgress === "Upload stopped by user."}
                >
                  {processingBulk ? "Stop Upload" : "Cancel"}
                </button>
                <button
                  className="btn-submit"
                  onClick={confirmAndUploadQR}
                  disabled={
                    processingBulk ||
                    qrSummaryData.filter(
                      (d) =>
                        d.isRecognized && !skippedStudents.has(d.studentId),
                    ).length === 0
                  }
                >
                  {processingBulk ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-cloud-arrow-up"></i> Approve &
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </Modal>

          {/* Smart Split Summary & PDF-like Document Viewer Modal */}
          <SmartSplitDocumentViewer
            isOpen={isSmartSplitModalOpen}
            onClose={() => {
              setIsSmartSplitModalOpen(false);
              setSmartSplitResult(null);
              setSmartSplitFile(null);
              setSmartSplitOriginalFile(null);
              setSmartSplitIsCaptured(false);
              setSmartSplitHasHeader(false);
              landscapeAbortRef.current = true;
              setLandscapePageCount(0);
              landscapePdfDocRef.current = null;
              setBulkUploadMethod(null);
              setIsBulkModalOpen(false);
            }}
            smartSplitResult={
              smartSplitResult
                ? {
                    ...smartSplitResult,
                    enrollments:
                      (smartSplitResult.enrollments?.length
                        ? smartSplitResult.enrollments
                        : students) || [],
                  }
                : null
            }
            testId={testId}
            onPageRotated={(pageNumber, newAngle) => {
              setSmartSplitResult((prev) => {
                if (!prev) return prev;
                const nextPages = (prev.pages || []).map((p) =>
                  p.pageNumber === pageNumber ? { ...p, rotation: newAngle } : p
                );
                return { ...prev, pages: nextPages };
              });
            }}
            onSave={(updatedPapers) => {
              setSmartSplitResult((prev) =>
                prev ? { ...prev, papers: updatedPapers } : prev
              );
            }}
            onSubmissionsUpdated={fetchPending}
          />
        </motion.div>
      )}
    </>
  );
}

